import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  CardBody,
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { patchUpdateActa, postActaAdicional, patchUpdateActaAdicional, deleteActaAdicional, getTarifasList, getContactsFilter, postImagenesInforme, getCasosRead, getValidarM3Gratis, postDeleteImages, getActividadesEconomicasFilter, postHomologarCuentas } from '../../helpers/backend';
import SignatureModal from '../../components/SignatureModal';
import { openActaVisitaPDF } from '../../components/ActaVisitaPDF';

const ActaVisita = ({ caso }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 7;
  const [activeTab, setActiveTab] = useState('1');

  // Funciones para conversión de formato de fecha
  const convertirFechaParaInput = (fechaString) => {
    if (!fechaString) return '';

    try {
      // Si ya está en formato yyyy-MM-dd, devolverla tal como está
      if (fechaString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return fechaString;
      }

      // Si viene en formato MM/dd/yyyy HH:mm AM/PM del backend, convertir correctamente
      if (fechaString.includes('/')) {
        // Extraer solo la parte de la fecha (sin hora)
        const fechaParte = fechaString.split(' ')[0]; // "01/09/2025"
        const [primerNumero, segundoNumero, año] = fechaParte.split('/');

        // Interpretar como dd/MM/yyyy donde primerNumero es día y segundoNumero es mes
        // Construir directamente el string YYYY-MM-DD para evitar problemas de zona horaria
        const dia = primerNumero.padStart(2, '0');
        const mes = segundoNumero.padStart(2, '0');

        // Validar que los valores sean válidos
        if (año && mes && dia &&
          parseInt(mes) >= 1 && parseInt(mes) <= 12 &&
          parseInt(dia) >= 1 && parseInt(dia) <= 31) {
          return `${año}-${mes}-${dia}`;
        }
      }

      return '';
    } catch (error) {
      console.error('Error al convertir fecha para input:', error);
      return '';
    }
  };

  const convertirFechaParaGuardar = (fechaInput) => {
    if (!fechaInput) return '';

    try {
      // Si viene en formato yyyy-MM-dd del input, mantenerlo así para el backend
      if (fechaInput.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return fechaInput; // El backend ahora requiere formato YYYY-MM-DD
      }

      // Si viene en otro formato, convertir a YYYY-MM-DD
      const fecha = new Date(fechaInput);
      if (!isNaN(fecha.getTime())) {
        return fecha.toISOString().split('T')[0]; // Formato YYYY-MM-DD
      }

      return fechaInput;
    } catch (error) {
      console.error('Error al convertir fecha para guardar:', error);
      return fechaInput;
    }
  };

  // Estados para gestión de múltiples actas
  const [actas, setActas] = useState([]);
  const [actaActualIndex, setActaActualIndex] = useState(0);
  const [nextActaId, setNextActaId] = useState(2);

  // Estados para los modales de firma
  const [showRepresentanteSignature, setShowRepresentanteSignature] = useState(false);
  const [showCiudadLimpiaSignature, setShowCiudadLimpiaSignature] = useState(false);

  // Estados para firmas temporales (antes de guardar)
  const [tempRepresentanteFirma, setTempRepresentanteFirma] = useState(null);
  const [tempCiudadLimpiaFirma, setTempCiudadLimpiaFirma] = useState(null);

  // Estados para búsqueda de contactos
  const [searchingContact, setSearchingContact] = useState(false);
  const [contactSearchError, setContactSearchError] = useState('');

  // Estados para las URLs de vista previa de las firmas

  // Estados para tarifas
  const [tarifas, setTarifas] = useState([]);
  const [tarifaActual, setTarifaActual] = useState(null);

  // Verificar si el caso está finalizado
  const isCasoFinalizado = caso?.estado === 'FINALIZADO';

  // Función para obtener URL de firma del servidor

  // Función para obtener tarifas del servidor
  const obtenerTarifas = async () => {
    try {
      const response = await getTarifasList();
      if (response && response.data && response.data.results) {
        console.log('🧾 [TARIFAS] Lista completa recibida del backend:', response.data.results);
        setTarifas(response.data.results);
        // Buscar la tarifa que coincida con la descripción del cliente
        buscarTarifaCliente(response.data.results);
      }
    } catch (error) {
      console.error('Error al obtener tarifas:', error);
    }
  };

  // Función para buscar la tarifa del cliente
  const buscarTarifaCliente = (listaTarifas) => {
    const descripcionCliente = caso?.cliente[0]?.catastros_ciudad_limpia[0]?.descripcion_tipoproductor;
    console.log('🔍 [TARIFAS] descripción del cliente:', descripcionCliente);

    if (descripcionCliente && listaTarifas) {
      const tarifaEncontrada = listaTarifas.find(tarifa =>
        tarifa.descripcion === descripcionCliente
      );

      if (tarifaEncontrada) {
        console.log('✅ [TARIFAS] Tarifa encontrada para el cliente:', tarifaEncontrada);
        console.log('💧 [TARIFAS] tarifa_lodo en tarifa:', tarifaEncontrada.tarifa_lodo);
        setTarifaActual(tarifaEncontrada);
      } else {
        console.warn('⚠️ [TARIFAS] No se encontró tarifa para la descripción:', descripcionCliente);
      }
    }
  };

  // Función para calcular el valor total de m3 - ahora recibe el acta como parámetro
  const calcularValorM3 = (acta = null) => {
    if (!tarifaActual) return 0;

    // Usar el acta pasado como parámetro o el actaVisita actual
    const actaParaCalcular = acta || actaVisita;

    // Calcular el total de m3 de todos los recipientes
    let recipientes = actaParaCalcular.recipientes_detalle || [];

    // Si es un string JSON, parsearlo
    if (typeof recipientes === 'string') {
      try {
        recipientes = JSON.parse(recipientes);
      } catch (e) {
        console.error('Error parsing recipientes_detalle JSON in calcularValorM3:', e);
        recipientes = [];
      }
    }

    // Verificar que sea un array
    if (!Array.isArray(recipientes)) {
      recipientes = [];
    }

    const totalM3 = recipientes.reduce((total, recipiente) => {
      return total + (parseFloat(recipiente.m3) || 0);
    }, 0);

    // Aplicar descuento de m³ gratis (1 o 2) solo si no es especial
    const descuentoM3 = (!actaParaCalcular.especiales)
      ? (actaParaCalcular.dos_m3_gratis ? 2 : (actaParaCalcular.m3_gratis ? 1 : 0))
      : 0;
    const m3Facturables = Math.max(0, totalM3 - descuentoM3);

    // tarifa_lodo tiene prioridad sobre especial/normal cuando está activo
    let tarifaAplicar;
    if (actaParaCalcular.tarifa_lodo && tarifaActual.tarifa_lodo) {
      tarifaAplicar = parseFloat(tarifaActual.tarifa_lodo);
    } else {
      tarifaAplicar = actaParaCalcular.especiales ? tarifaActual.tarifa_especial : tarifaActual.valor_tarifa;
    }

    return (m3Facturables * (tarifaAplicar || 0)).toFixed(2);
  };

  const parseArray = (arr) => {
    if (!arr) return [];
    if (Array.isArray(arr)) return arr;
    if (typeof arr === 'string') {
      try {
        const parsed = JSON.parse(arr);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    }
    return [];
  };

  const hasAnyDataInRows = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return false;
    return rows.some(row => {
      return Object.values(row || {}).some(v => v !== '' && v !== null && v !== undefined);
    });
  };

  const hasStep2Data = () => {
    const ur = parseArray(actaVisita?.unidades_residenciales_detalle);
    const unr = parseArray(actaVisita?.unidades_no_residenciales_detalle);
    const productoDetalle = parseArray(actaVisita?.producto_detalle);
    const productoNombreFilled = actaVisita?.producto_nombre !== '' && actaVisita?.producto_nombre !== null && actaVisita?.producto_nombre !== undefined;
    const productoUnrFilled = actaVisita?.producto_unr !== '' && actaVisita?.producto_unr !== null && actaVisita?.producto_unr !== undefined;
    return (
      hasAnyDataInRows(ur) ||
      hasAnyDataInRows(unr) ||
      hasAnyDataInRows(productoDetalle) ||
      productoNombreFilled ||
      productoUnrFilled
    );
  };

  const hasStep4ChecksMarked = () => {
    const fields = [
      'abandonado', 'suscriptor_inactivo', 'demolido', 'en_construccion',
      'en_remodelacion', 'inexistente', 'inhabitable_o_en_ruinas', 'lote',
      'minimas_condiciones', 'programa_del_icbf', 'descuento_puerta_a_puerta',
      'se_cierra_pqr_como_no_atendido', 'areas_comunes', 'predio_que_se_observa_vacio',
      'predio_se_observa_ocupado', 'cuenta_suscriptor_activo'
    ];
    return fields.some(f => !!actaVisita?.[f]);
  };

  // Validaciones por tipo_orden
  const hasURData = (acta = actaVisita) => {
    const ur = parseArray(acta?.unidades_residenciales_detalle);
    return hasAnyDataInRows(ur);
  };

  const hasUNRData = (acta = actaVisita) => {
    const unr = parseArray(acta?.unidades_no_residenciales_detalle);
    return hasAnyDataInRows(unr);
  };

  const hasCaracteristicasPredioData = (acta = actaVisita) => {
    const fields = [
      'abandonado', 'suscriptor_inactivo', 'demolido', 'en_construccion',
      'en_remodelacion', 'inexistente', 'inhabitable_o_en_ruinas', 'lote',
      'minimas_condiciones', 'programa_del_icbf', 'descuento_puerta_a_puerta',
      'se_cierra_pqr_como_no_atendido', 'areas_comunes', 'predio_que_se_observa_vacio',
      'predio_se_observa_ocupado', 'cuenta_suscriptor_activo'
    ];
    return fields.some(f => !!acta?.[f]);
  };

  const hasTiposResiduosRecipientes = (acta = actaVisita) => {
    return !!(
      acta?.escombros || acta?.especiales || acta?.para_relleno || acta?.para_escombrera ||
      acta?.enlonado || acta?.a_granel || (acta?.tipo_recoleccion && String(acta.tipo_recoleccion).trim() !== '')
    );
  };

  const hasDetallesRecipientes = (acta = actaVisita) => {
    const rec = parseArray(acta?.recipientes_detalle);
    return hasAnyDataInRows(rec);
  };

  const validarPorTipoOrden = (acta = actaVisita) => {
    const tipoOrden = caso?.tipo_orden;
    if (tipoOrden === 'PQRS') {
      if (!(hasURData(acta) || hasUNRData(acta) || hasCaracteristicasPredioData(acta))) {
        Swal.fire({
          icon: 'warning',
          title: 'Faltan datos requeridos (PQRS)',
          text: 'Para tipo de orden PQRS, diligencie al menos uno: Unidades Residenciales (UR), Unidades No Residenciales (UNR) o Características del Predio.',
          confirmButtonColor: '#dc3545'
        });
        return false;
      }
    } else if (tipoOrden === 'RCD') {
      // Si está cancelado, no aplicar la validación RCD
      if (acta?.cancelado) {
        return true;
      }
      if (!(hasTiposResiduosRecipientes(acta) && hasDetallesRecipientes(acta))) {
        Swal.fire({
          icon: 'warning',
          title: 'Faltan datos requeridos (RCD)',
          text: 'Para tipo de orden RCD, diligencie ambos: Tipos de Residuos y Recipientes, y Detalles de Recipientes. Si el acta está cancelada, esta validación no aplica.',
          confirmButtonColor: '#dc3545'
        });
        return false;
      }
    }
    return true;
  };

  const isStepBlocked = (stepNumber) => {
    const tipoCaso = caso?.tipo_caso;

    if (!actaVisita?.esPrincipal) {
      return false;
    }

    if (stepNumber === 4 && hasStep2Data()) {
      return true;
    }
    if (stepNumber === 2 && hasStep4ChecksMarked()) {
      return true;
    }

    if (tipoCaso === 'ESCOMBROS' && (stepNumber === 2 || stepNumber === 4)) {
      return true;
    }
    if (tipoCaso === 'ESPECIALES' && (stepNumber === 2 || stepNumber === 4)) {
      return true;
    }

    if (tipoCaso !== 'ESCOMBROS' && tipoCaso !== 'ESPECIALES' && stepNumber === 5) {
      return true;
    }

    return false;
  };

  // Obtener URL base para el PDF
  const getBaseUrl = () => {
    const URL = process.env.REACT_APP_API_URL;
    const url = URL.split("/api");
    return url[0];
  };

  // Estados para subir imágenes del informe
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]); // {file, preview, name, size}
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [existingImages, setExistingImages] = useState([]); // imágenes ya cargadas para el acta actual
  const [isLoadingExistingImages, setIsLoadingExistingImages] = useState(false);
  const [selectedImageIds, setSelectedImageIds] = useState([]); // selección para eliminación múltiple
  const [isDeletingImages, setIsDeletingImages] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const isAdministrador = React.useMemo(() => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return false;
      const user = JSON.parse(userStr);
      const role = user?.roles || user?.role || user?.userRole;
      if (typeof role === 'string') {
        return role === 'Administrador' || role === 'SuperAdmin';
      }
      if (Array.isArray(role)) {
        return role.includes('Administrador') || role.includes('SuperAdmin');
      }
      // Fallback usando groups si viene en otro formato
      const groups = user?.groups;
      if (typeof groups === 'string') {
        return groups.includes('Administrador') || groups.includes('SuperAdmin');
      }
      if (Array.isArray(groups)) {
        return groups.includes('Administrador') || groups.includes('SuperAdmin');
      }
      return false;
    } catch (e) {
      return false;
    }
  }, []);

  // Control de cálculo/edición de Valor m³
  const [usarCalculoValorM3, setUsarCalculoValorM3] = useState(false);

  // Estado local del caso para mantener la información siempre fresca
  const [casoData, setCasoData] = useState(caso);
  // Información y restricciones de m³ gratis
  const [ocultarChecksM3, setOcultarChecksM3] = useState(false);
  const [motivoRestriccionM3, setMotivoRestriccionM3] = useState(null);
  const [permitir1m3, setPermitir1m3] = useState(true);
  const [permitir2m3, setPermitir2m3] = useState(true);
  const [ultimaFechaOtorgado, setUltimaFechaOtorgado] = useState(null);
  const [proximoDesde, setProximoDesde] = useState(null);

  // Estados para homologación de cuenta Enel
  const [showHomologarModal, setShowHomologarModal] = useState(false);
  const [nuevaCuentaEnel, setNuevaCuentaEnel] = useState("");

  useEffect(() => {
    setCasoData(caso);
  }, [caso]);

  useEffect(() => {
    if (!showUploadModal) {
      // Al cerrar: limpiar todo el estado del modal de fotos
      setSelectedImageIds([]);
      setSelectedFiles([]);
      setUploadProgress(0);
      setIsUploading(false);
      setExistingImages([]);
    } else {
      // Al abrir: refrescar datos del caso y cargar imágenes existentes
      refrescarCaso().then(() => {
        loadExistingImagesForCurrentActa();
      });
    }
  }, [showUploadModal]);

  // Refrescar datos del caso desde el backend después de guardar o subir imágenes
  const refrescarCaso = async () => {
    try {
      if (!caso?.id) return;
      const res = await getCasosRead(caso.id);
      if (res && res.data) {
        setCasoData(res.data);
      }
    } catch (error) {
      console.error('No se pudo refrescar el caso:', error);
    }
  };

  // Función para homologar cuenta Enel
  const handleSubmitHomologacion = async () => {
    if (!nuevaCuentaEnel.trim() || !/^\d+$/.test(nuevaCuentaEnel)) {
      Swal.fire({
        title: 'Error',
        text: 'Por favor ingrese un número de cuenta válido',
        icon: 'error'
      });
      return;
    }

    try {
      const formData = new FormData();
      formData.append('nueva_cuenta_enel', nuevaCuentaEnel);

      const response = await postHomologarCuentas(caso.id, formData);
      console.log(response);

      if (response.data && response.data.success) {
        const resultado = response.data.resultado;
        await Swal.fire({
          title: 'Éxito',
          text: response.data.message,
          icon: 'success',
          html: `
            <div class="text-start">
              <p><strong>Detalles de la homologación:</strong></p>
              <p><strong>Origen:</strong></p>
              <ul>
                <li>Nombre: ${resultado.origen_nombre}</li>
                <li>Cuenta anterior: ${resultado.origen_cuenta_enel_anterior}</li>
              </ul>
              <p><strong>Destino:</strong></p>
              <ul>
                <li>Nombre: ${resultado.destino_nombre}</li>
                <li>Cuenta anterior: ${resultado.destino_cuenta_enel_anterior}</li>
                <li>Nueva cuenta: ${resultado.nueva_cuenta_enel}</li>
              </ul>
            </div>
          `
        });
        setShowHomologarModal(false);
        setNuevaCuentaEnel("");
        // Refrescar datos del caso para actualizar la cuenta
        await refrescarCaso();
      } else {
        throw new Error(response.data?.message || 'No se recibió confirmación del servidor');
      }
    } catch (error) {
      console.error("Error al homologar cuenta:", error);
      Swal.fire({
        title: 'Error',
        text: 'No se pudo procesar la homologación. Por favor intente nuevamente.',
        icon: 'error'
      });
    }
  };

  // Helpers para manejo de archivos
  const processFiles = (files) => {
    const filesWithPreviews = [];
    let processedCount = 0;

    files.forEach((file, index) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        filesWithPreviews[index] = {
          file: file,
          preview: e.target.result,
          name: file.name,
          size: file.size
        };

        processedCount++;
        if (processedCount === files.length) {
          setSelectedFiles(prev => [...prev, ...filesWithPreviews]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  // Función para solicitar permisos de cámara
  const requestCameraPermission = async () => {
    try {
      // Verificar si el navegador soporta getUserMedia
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        Swal.fire({
          icon: 'error',
          title: 'Cámara no disponible',
          text: 'Tu navegador no soporta el acceso a la cámara. Por favor, usa un navegador más reciente.',
          confirmButtonColor: '#dc3545'
        });
        return false;
      }

      // Solicitar permisos de cámara
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: 'environment' } } // Preferir cámara trasera cuando esté disponible
      });

      return stream;
    } catch (error) {
      console.error('Error al solicitar permisos de cámara:', error);

      let mensaje = 'No se pudo acceder a la cámara.';
      if (error.name === 'NotAllowedError') {
        mensaje = 'Permisos de cámara denegados. Por favor, permite el acceso a la cámara en la configuración del navegador.';
      } else if (error.name === 'NotFoundError') {
        mensaje = 'No se encontró ninguna cámara en este dispositivo.';
      } else if (error.name === 'NotReadableError') {
        mensaje = 'La cámara está siendo usada por otra aplicación.';
      }

      Swal.fire({
        icon: 'warning',
        title: 'Acceso a cámara',
        text: mensaje,
        confirmButtonColor: '#2fa4e7'
      });

      return false;
    }
  };

  // Función para abrir la cámara en modal
  const handleCameraClick = async () => {
    const stream = await requestCameraPermission();
    if (stream) {
      setCameraStream(stream);
      setShowCameraModal(true);
      // Intentar reproducir el video cuando el modal esté montado
      setTimeout(() => {
        if (videoRef.current) {
          try {
            videoRef.current.srcObject = stream;
            const playPromise = videoRef.current.play();
            if (playPromise && typeof playPromise.then === 'function') {
              playPromise.catch((e) => console.warn('No se pudo iniciar reproducción de la cámara:', e));
            }
          } catch (e) {
            console.warn('Error iniciando cámara:', e);
          }
        }
      }, 50);
    }
  };

  // Función para cerrar la cámara
  const closeCameraModal = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    if (videoRef.current) {
      try {
        videoRef.current.pause();
        videoRef.current.srcObject = null;
      } catch (e) { }
    }
    setShowCameraModal(false);
  };

  // Función para tomar foto
  const takePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      // Configurar el canvas con las dimensiones del video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Dibujar el frame actual del video en el canvas
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convertir a blob y crear archivo
      canvas.toBlob((blob) => {
        const timestamp = new Date().getTime();
        const file = new File([blob], `camera-photo-${timestamp}.jpg`, { type: 'image/jpeg' });

        // Crear preview
        const reader = new FileReader();
        reader.onload = (e) => {
          const newFile = {
            file: file,
            preview: e.target.result,
            name: file.name,
            size: file.size
          };
          setSelectedFiles(prev => [...prev, newFile]);
        };
        reader.readAsDataURL(file);

        // Cerrar modal de cámara
        closeCameraModal();
      }, 'image/jpeg', 0.8);
    }
  };

  // Efecto para inicializar el video cuando se abre el modal
  useEffect(() => {
    if (showCameraModal && cameraStream && videoRef.current) {
      videoRef.current.srcObject = cameraStream;
      // Asegurar reproducción en navegadores que requieren llamada explícita
      const onLoaded = () => {
        try {
          const playPromise = videoRef.current.play();
          if (playPromise && typeof playPromise.then === 'function') {
            playPromise.catch((e) => console.warn('No se pudo reproducir la cámara:', e));
          }
        } catch (e) {
          console.warn('Error al reproducir la cámara:', e);
        }
      };
      // Si ya tiene metadatos cargados, intentar reproducir; si no, esperar evento
      if (videoRef.current.readyState >= 2) {
        onLoaded();
      } else {
        videoRef.current.onloadedmetadata = onLoaded;
      }
    }
  }, [showCameraModal, cameraStream]);

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files).filter(f => f.type.startsWith('image/'));
    if (files.length > 0) {
      processFiles(files);
    }
    // Limpiar el valor para permitir volver a seleccionar el mismo archivo
    event.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files).filter(file =>
      file.type.startsWith('image/')
    );

    if (files.length > 0) {
      processFiles(files);
    }
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const toggleSelectImage = (id) => {
    if (!id) return;
    setSelectedImageIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const deleteImages = async (ids) => {
    const informeId = getInformeIdForCurrentActa();
    if (!informeId || !Array.isArray(ids) || ids.length === 0) return;

    // Filtrar solo IDs reales (no temporales)
    const realIds = ids.filter(id => id && !String(id).startsWith('temp-'));
    if (realIds.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Sin imágenes válidas',
        text: 'No hay imágenes válidas para eliminar.',
        confirmButtonColor: '#ffc107'
      });
      return;
    }

    setIsDeletingImages(true);
    try {
      const payload = { informe_id: informeId, image_ids: realIds };
      const res = await postDeleteImages(payload);
      const ok = res?.status === 200 || res?.status === 204 || res?.status === 201;
      if (ok) {
        Swal.fire({
          icon: 'success',
          title: 'Imágenes eliminadas',
          text: `Se eliminaron ${realIds.length} imagen(es)`,
          confirmButtonColor: '#28a745',
          timer: 2000,
          timerProgressBar: true
        });
        await refrescarCaso();
        loadExistingImagesForCurrentActa();
        setSelectedImageIds([]);
      } else {
        throw new Error('Respuesta inesperada del servidor');
      }
    } catch (error) {
      console.error('Error eliminando imágenes:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error al eliminar',
        text: 'No se pudieron eliminar las imágenes seleccionadas.',
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setIsDeletingImages(false);
    }
  };

  const deleteSingleImage = async (id) => {
    if (!id) return;
    await deleteImages([id]);
  };

  const getInformeIdForCurrentActa = () => {
    if (actaVisita.esPrincipal) {
      return casoData?.informe?.id || null;
    }
    return actaVisita.servidorId || null; // para acta adicional existente
  };

  // Resolver la URL de una imagen (acepta string u objeto {image} / {image_url})
  const resolveImageUrl = (img) => {
    if (!img) return null;
    if (typeof img === 'object') {
      const path = img.image || img.image_url;
      return resolveImageUrl(path);
    }
    if (typeof img === 'string') {
      const trimmed = img.trim();
      if (!trimmed) return null;
      if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
        return trimmed;
      }
      const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
      return `${getBaseUrl()}${path}`;
    }
    return null;
  };

  // Obtener las imágenes ya cargadas para el acta actual desde casoData
  const loadExistingImagesForCurrentActa = () => {
    try {
      setIsLoadingExistingImages(true);
      let imgs = [];
      if (actaVisita.esPrincipal) {
        imgs = Array.isArray(casoData?.informe?.images) ? casoData.informe.images : [];
      } else {
        const adicionales = Array.isArray(casoData?.informes_adicionales) ? casoData.informes_adicionales : [];
        const informeActual = adicionales.find((inf) => inf.id === actaVisita.servidorId);
        imgs = Array.isArray(informeActual?.images) ? informeActual.images : [];
      }
      setExistingImages(imgs);
    } catch (e) {
      console.error('No se pudieron cargar las imágenes existentes del informe:', e);
      setExistingImages([]);
    } finally {
      setIsLoadingExistingImages(false);
    }
  };

  // (Movido más abajo) Cargar/actualizar previews existentes cuando se abre el modal o cambia casoData

  const handleImageUpload = async () => {
    if (selectedFiles.length === 0) return;

    const informeId = getInformeIdForCurrentActa();

    if (!informeId) {
      const mensaje = actaVisita.esPrincipal
        ? 'No se encontró el ID del informe principal. Por favor guarde el acta o recargue los datos del caso.'
        : 'Debe guardar el Acta Adicional antes de subir fotos.';
      Swal.fire({
        icon: 'warning',
        title: 'Acción requerida',
        text: mensaje,
        confirmButtonColor: '#2fa4e7'
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Crear FormData para enviar archivos
      const formData = new FormData();

      // Agregar el informe_id
      formData.append('informe_id', informeId);

      // Agregar cada archivo al FormData
      selectedFiles.forEach((fileObj, index) => {
        formData.append('images', fileObj.file);
      });

      // Simular progreso de preparación
      setUploadProgress(20);

      const response = await postImagenesInforme(formData);

      setUploadProgress(100);

      if (response.status === 200 || response.status === 201) {
        Swal.fire({
          icon: 'success',
          title: '¡Éxito!',
          text: `${selectedFiles.length} imagen(es) subida(s) correctamente al informe`,
          confirmButtonColor: '#28a745',
          timer: 2500,
          timerProgressBar: true
        });
        setSelectedFiles([]);
        setShowUploadModal(false);
        // Refrescar el caso para que el PDF incluya las nuevas imágenes
        await refrescarCaso();
      } else {
        throw new Error('Respuesta inválida del servidor');
      }
    } catch (error) {
      console.error('Error subiendo imágenes del informe:', error);
      setUploadProgress(0);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Hubo un problema al subir las imágenes. Intente nuevamente.',
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    if (caso) {
      console.log(caso);

    }
  }, [caso]);
  // Función para crear un acta vacía
  const crearActaVacia = (id = 1, esPrincipal = true) => {
    const now = new Date();
    const fechaActual = now.toISOString().split('T')[0]; // Formato YYYY-MM-DD
    const horaActual = now.toTimeString().slice(0, 5); // Formato HH:MM

    return {
      id,
      esPrincipal,
      titulo: esPrincipal ? 'Acta Principal' : `Acta Adicional ${id}`,
      // Control de estado para actas adicionales
      esNueva: !esPrincipal, // Las actas adicionales empiezan como nuevas
      servidorId: null, // ID asignado por el servidor después de guardar
      // Información de visita
      fecha_visita: fechaActual,
      fecha_informe: fechaActual, // Nueva fecha para el informe que se puede modificar
      hora_visita: horaActual,
      ciudad_visita: 'Bogotá',
      dia_visita: '',
      mes_visita: '',
      año_visita: '',
      archivo_no: '',
      no_pqrs: '',
      // Campos específicos de cada acta (no heredados)
      cuenta_suscriptor: '',
      cuenta_energia: '',
      direccion: '',
      nombre_cliente: '',
      direccion_informe: '',

      // Nuevas estructuras de datos dinámicas
      unidades_residenciales_detalle: [],
      producto_nombre: '',
      producto_detalle: [],
      unidades_no_residenciales_detalle: [],
      recipientes_detalle: [],

      // Campos legacy mantenidos para compatibilidad
      ur_piso: '',
      ur: '',
      ur_ocupadas: '',
      ur_vacias: '',
      producto_piso: '',
      producto_ur: '',
      producto_ocupadas: '',
      producto_vacias: '',
      unr_piso: '',
      unr: '',
      unr_actividad_nombre: '',
      producto_unr: '',
      unr_largo: '',
      unr_ancho: '',
      unr_area: '',
      unr_ocup: '',
      unr_vacias: '',

      // Actualización de datos
      direccion_predio: '',
      estrato_predio: '',
      localidad_predio: '',
      actualizacion_nombre_suscriptor: '',
      actualizacion_numero_meses_financiar: '',

      // Características del predio (checkboxes)
      abandonado: false,
      suscriptor_inactivo: false,
      demolido: false,
      causa_demolicion: '',
      en_construccion: false,
      en_remodelacion: false,
      inexistente: false,
      inhabitable_o_en_ruinas: false,
      lote: false,
      minimas_condiciones: false,
      programa_del_icbf: false,
      descuento_puerta_a_puerta: false,
      se_cierra_pqr_como_no_atendido: false,
      areas_comunes: false,
      predio_que_se_observa_vacio: false,
      predio_se_observa_ocupado: false,
      cuenta_suscriptor_activo: false,

      // Tipos de residuos
      escombros: false,
      especiales: false,
      para_relleno: false,
      para_escombrera: false,
      enlonado: false,
      a_granel: false,
      tarifa_lodo: false,
      tipo_recoleccion: '',

      // Información de recipientes
      descripcion_recipiente: '',
      recipiente_largo: '',
      recipiente_ancho: '',
      recipiente_alto: '',
      recipiente_cantidad: '',
      recipiente_m3: '',
      m3_gratis: false,
      dos_m3_gratis: false,
      cancelado: false,
      valor_m3: '',



      // Observaciones
      observaciones: '',

      // Representante usuario
      representante_nombre: '',
      representante_cedula: '',
      representante_telefono_celular: '',
      representante_firma: null,
      anotaciones: '',
      sin_firma: false,

      // Representante ciudad limpia
      ciudad_limpia_nombre: '',
      ciudad_limpia_codigo: '',
      ciudad_limpia_telefono: '',
      ciudad_limpia_firma: null
    };
  };

  // Estado para el acta actual (referencia al acta seleccionada)
  const [actaVisita, setActaVisita] = useState(() => crearActaVacia(1, true));

  // Inicializar actas con el acta principal
  useEffect(() => {
    const actaPrincipal = crearActaVacia(1, true);
    setActas([actaPrincipal]);
    setActaVisita(actaPrincipal);
  }, []);

  // Cargar tarifas al montar el componente
  useEffect(() => {
    obtenerTarifas();
  }, []);

  // Extraer cuenta_contrato de forma robusta
  const cuentaContrato = React.useMemo(() => {
    const c = casoData || caso;
    return (
      c?.cuenta_contrato ||
      c?.contrato ||
      c?.cliente?.[0]?.cuenta_contrato ||
      c?.cliente?.cuenta_contrato ||
      c?.cliente?.[0]?.catastros_ciudad_limpia?.[0]?.cuenta_contrato ||
      null
    );
  }, [casoData, caso]);

  // Consultar validación de m³ gratis cuando la pestaña de Recipientes está activa
  useEffect(() => {
    const isRecipientesTab = currentStep === 5 || activeTab === '5' || activeTab === 5;
    if (!isRecipientesTab) return;
    if (!cuentaContrato) return;

    const fetchValidacion = async () => {
      try {
        const res = await getValidarM3Gratis(cuentaContrato);
        const data = res?.data || res;
        console.log('Validación m3 gratis:', data);
        const solicitudes = data?.limite_mensual?.solicitudes_actuales;
        const limiteMensual = data?.limite_mensual?.limite_solicitudes_mensual;
        const bloquearMensual = data?.limite_mensual?.bloqueado === true || (solicitudes !== undefined && limiteMensual !== undefined && solicitudes >= limiteMensual);
        const puedeOtorgar180 = data?.limite_180_dias?.puede_otorgar;
        const motivo180 = data?.limite_180_dias?.motivo || null;
        const bloquearPor180 = puedeOtorgar180 === false;

        // nuevas llaves para permisos individuales
        const p1 = data?.limite_180_dias?.puede_1m3;
        const p2 = data?.limite_180_dias?.puede_2m3;
        setPermitir1m3(p1 === undefined ? true : !!p1);
        setPermitir2m3(p2 === undefined ? true : !!p2);
        setUltimaFechaOtorgado(data?.limite_180_dias?.ultima_fecha_otorgado || null);
        setProximoDesde(data?.limite_180_dias?.proximo_desde || null);

        const bloquear = !!bloquearMensual || !!bloquearPor180;
        // Ocultar si está bloqueado y el caso no ha finalizado para evitar nuevas selecciones
        const bloquearEstaActa = bloquear && !isCasoFinalizado;
        setOcultarChecksM3(bloquearEstaActa);

        // Priorizar el motivo mensual si está bloqueado, sino el de 180 días
        let motivoActual = null;
        if (bloquearMensual) {
          motivoActual = data?.limite_mensual?.motivo || 'Límite mensual alcanzado para m³ gratis.';
        } else if (bloquearPor180) {
          motivoActual = motivo180 || 'No es posible otorgar m³ gratis por restricción de 180 días.';
        }
        setMotivoRestriccionM3(motivoActual);
      } catch (error) {
        console.error('Error al validar m3 gratis:', error);
        setOcultarChecksM3(false);
        setMotivoRestriccionM3(null);
        setPermitir1m3(true);
        setPermitir2m3(true);
        setUltimaFechaOtorgado(null);
        setProximoDesde(null);
      }
    };
    fetchValidacion();
  }, [currentStep, activeTab, cuentaContrato]);

  // Cargar/actualizar previews existentes cuando se abre el modal o cambia casoData/acta actual
  useEffect(() => {
    if (showUploadModal) {
      loadExistingImagesForCurrentActa();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showUploadModal, casoData, actaVisita?.servidorId]);

  // Calcular suma total de m3 para usar como dependencia
  const totalM3Recipientes = React.useMemo(() => {
    let recipientes = actaVisita.recipientes_detalle;

    // Si es un string JSON, parsearlo
    if (typeof recipientes === 'string') {
      try {
        recipientes = JSON.parse(recipientes);
      } catch (e) {
        console.error('Error parsing recipientes_detalle JSON:', e);
        return 0;
      }
    }

    // Verificar que sea un array
    if (!Array.isArray(recipientes)) {
      return 0;
    }

    return recipientes.reduce((total, recipiente) => {
      return total + (parseFloat(recipiente.m3) || 0);
    }, 0);
  }, [actaVisita.recipientes_detalle]);

  // Totales de UR y UNR para comparación con referencia
  const totalURCalculado = React.useMemo(() => {
    const ur = parseArray(actaVisita?.unidades_residenciales_detalle);
    return ur.reduce((acc, row) => acc + (parseInt(row.ur) || 0), 0);
  }, [actaVisita?.unidades_residenciales_detalle]);

  const totalUNRCalculado = React.useMemo(() => {
    const unr = parseArray(actaVisita?.unidades_no_residenciales_detalle);
    return unr.reduce((acc, row) => acc + (parseInt(row.unr) || 0), 0);
  }, [actaVisita?.unidades_no_residenciales_detalle]);

  // Recalcular valor_m3 cuando cambian los datos relevantes - ahora funciona para cualquier acta
  useEffect(() => {
    // Solo recalcular si el usuario activó el cálculo automático y no está en modo manual
    if (!tarifaActual) return;
    if (!usarCalculoValorM3) return;

    const nuevoValor = calcularValorM3(actaVisita);
    const valorActual = parseFloat(actaVisita.valor_m3 || 0).toFixed(2);

    // Permitir actualizar incluso a 0 cuando el cálculo lo determine
    if (nuevoValor !== valorActual) {
      setTimeout(() => {
        actualizarActaActual({ valor_m3: nuevoValor });
      }, 100);
    }

  }, [tarifaActual, totalM3Recipientes, actaVisita.m3_gratis, actaVisita.dos_m3_gratis, actaVisita.especiales, actaVisita.tarifa_lodo, actaVisita.id, usarCalculoValorM3]);

  // Reiniciar modo cálculo/manual al cambiar de acta
  useEffect(() => {
    setUsarCalculoValorM3(false);
  }, [actaVisita.id]);

  // Inicializar recipientes_detalle con una fila vacía si no hay datos
  useEffect(() => {
    if (!isCasoFinalizado &&
      actaVisita.recipientes_detalle &&
      Array.isArray(actaVisita.recipientes_detalle) &&
      actaVisita.recipientes_detalle.length === 0) {
      const newRow = {
        descripcion: '', largo: '', ancho: '', alto: '', cantidad: '', m3: ''
      };
      actualizarActaActual({ recipientes_detalle: [newRow] });
    }
  }, [isCasoFinalizado, actaVisita.id]); // Removida la dependencia problemática

  // Función para generar vista previa del PDF
  const openPdf = async () => {
    try {
      // Mostrar loading
      Swal.fire({
        title: 'Generando PDF...',
        text: 'Por favor espera mientras se genera el documento',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Preparar datos para el PDF usando el acta actual
      const actaData = {
        // Información básica
        fecha_visita: actaVisita.fecha_visita || casoData?.informe?.fecha_visita || new Date().toISOString().split('T')[0],
        fecha_informe: actaVisita.fecha_informe || casoData?.informe?.fecha_informe || new Date().toISOString().split('T')[0],
        hora_visita: actaVisita.hora_visita || casoData?.informe?.hora_visita || new Date().toTimeString().slice(0, 5),
        ciudad_visita: actaVisita.ciudad_visita || casoData?.informe?.ciudad_visita || 'Bogotá',
        dia_visita: actaVisita.dia_visita || casoData?.informe?.dia_visita || new Date().getDate().toString(),
        mes_visita: actaVisita.mes_visita || casoData?.informe?.mes_visita || new Date().toLocaleDateString('es-ES', { month: 'long' }),
        año_visita: actaVisita.año_visita || casoData?.informe?.año_visita || new Date().getFullYear().toString(),
        archivo_no: actaVisita.archivo_no || '',
        no_pqrs: actaVisita.no_pqrs || caso.numero_reclamo || '',
        cuenta_suscriptor: actaVisita.cuenta_suscriptor || '',
        cuenta_energia: actaVisita.cuenta_energia || '',
        direccion: actaVisita.direccion || caso.cliente?.[0].address?.[0].address || '',

        // Datos del predio
        direccion_predio: actaVisita.direccion_predio || '',
        estrato_predio: actaVisita.estrato_predio || '',
        localidad_predio: actaVisita.localidad_predio || '',
        actualizacion_nombre_suscriptor: actaVisita.actualizacion_nombre_suscriptor || '',

        // Tablas dinámicas
        unidades_residenciales_detalle: actaVisita.unidades_residenciales_detalle ?
          (typeof actaVisita.unidades_residenciales_detalle === 'string' ?
            JSON.parse(actaVisita.unidades_residenciales_detalle) :
            actaVisita.unidades_residenciales_detalle) : [],
        producto_nombre: actaVisita.producto_nombre || '',
        producto_detalle: actaVisita.producto_detalle ?
          (typeof actaVisita.producto_detalle === 'string' ?
            JSON.parse(actaVisita.producto_detalle) :
            actaVisita.producto_detalle) : [],
        unidades_no_residenciales_detalle: actaVisita.unidades_no_residenciales_detalle ?
          (typeof actaVisita.unidades_no_residenciales_detalle === 'string' ?
            JSON.parse(actaVisita.unidades_no_residenciales_detalle) :
            actaVisita.unidades_no_residenciales_detalle) : [],
        producto_unr: actaVisita.producto_unr || '',
        recipientes_detalle: actaVisita.recipientes_detalle ?
          (typeof actaVisita.recipientes_detalle === 'string' ?
            JSON.parse(actaVisita.recipientes_detalle) :
            actaVisita.recipientes_detalle) : [],

        // Características del predio
        abandonado: actaVisita.abandonado || false,
        suscriptor_inactivo: actaVisita.suscriptor_inactivo || false,
        demolido: actaVisita.demolido || false,
        en_construccion: actaVisita.en_construccion || false,
        en_remodelacion: actaVisita.en_remodelacion || false,
        inexistente: actaVisita.inexistente || false,
        inhabitable_o_en_ruinas: actaVisita.inhabitable_o_en_ruinas || false,
        lote: actaVisita.lote || false,
        minimas_condiciones: actaVisita.minimas_condiciones || false,
        programa_del_icbf: actaVisita.programa_del_icbf || false,
        descuento_puerta_a_puerta: actaVisita.descuento_puerta_a_puerta || false,
        se_cierra_pqr_como_no_atendido: actaVisita.se_cierra_pqr_como_no_atendido || false,
        areas_comunes: actaVisita.areas_comunes || false,
        predio_que_se_observa_vacio: actaVisita.predio_que_se_observa_vacio || false,
        predio_se_observa_ocupado: actaVisita.predio_se_observa_ocupado || false,
        cuenta_suscriptor_activo: actaVisita.cuenta_suscriptor_activo || false,
        causa_demolicion: actaVisita.causa_demolicion || "",
        suscriptor_activo_detalles: actaVisita.suscriptor_activo_detalles || "",

        // Tipos de residuos
        tipos_residuos: {
          escombros: actaVisita.escombros || false,
          especiales: actaVisita.especiales || false,
          para_reuso: actaVisita.para_relleno || false,
          para_escombrera: actaVisita.para_escombrera || false,
          enlonado: actaVisita.enlonado || false,
          a_granel: actaVisita.a_granel || false
        },
        tipo_recoleccion: actaVisita.tipo_recoleccion || '',
        m3_gratis: actaVisita.m3_gratis || false,
        dos_m3_gratis: actaVisita.dos_m3_gratis || false,
        valor_m3: actaVisita.valor_m3 || '',
        cancelado: actaVisita.cancelado || false,

        // Observaciones y representantes
        observaciones: actaVisita.observaciones || '',
        nombre_cliente: actaVisita.nombre_cliente || '',
        direccion_informe: actaVisita.direccion_informe || '',
        representante_nombre: actaVisita.representante_nombre || '',
        representante_cedula: actaVisita.representante_cedula || '',
        representante_firma: actaVisita.representante_firma,
        representante_telefono_celular: actaVisita.representante_telefono_celular || '',
        ciudad_limpia_nombre: actaVisita.ciudad_limpia_nombre || '',
        ciudad_limpia_telefono: actaVisita.ciudad_limpia_telefono || '',
        ciudad_limpia_codigo: actaVisita.ciudad_limpia_codigo || '',
        ciudad_limpia_firma: actaVisita.ciudad_limpia_firma || '',
        anotaciones: actaVisita.anotaciones || '',
        sin_firma: actaVisita.sin_firma || false
      };

      // Preparar actas adicionales para el PDF
      const actasAdicionales = actas.filter(acta => !acta.esPrincipal).map(acta => ({
        id: acta.servidorId || acta.id,
        titulo: acta.titulo,
        fecha_visita: acta.fecha_visita,
        fecha_informe: acta.fecha_informe,
        hora_visita: acta.hora_visita,
        ciudad_visita: acta.ciudad_visita,
        dia_visita: acta.dia_visita,
        mes_visita: acta.mes_visita,
        año_visita: acta.año_visita,
        archivo_no: acta.archivo_no,
        no_de_pqrs: acta.no_pqrs,
        cuenta_suscriptor: acta.cuenta_suscriptor,
        cuenta_energia: acta.cuenta_energia,
        nombre_cliente: acta.nombre_cliente,
        direccion: acta.direccion,
        direccion_informe: acta.direccion_informe,

        // Estructuras de datos dinámicas
        unidades_residenciales_detalle: acta.unidades_residenciales_detalle,
        producto_nombre: acta.producto_nombre,
        producto_detalle: acta.producto_detalle,
        unidades_no_residenciales_detalle: acta.unidades_no_residenciales_detalle,
        recipientes_detalle: acta.recipientes_detalle,

        // Campos legacy
        ur_piso: acta.ur_piso,
        ur: acta.ur,
        ur_ocupadas: acta.ur_ocupadas,
        ur_vacias: acta.ur_vacias,
        producto_piso: acta.producto_piso,
        producto_ur: acta.producto_ur,
        producto_ocupadas: acta.producto_ocupadas,
        producto_vacias: acta.producto_vacias,
        unr_piso: acta.unr_piso,
        unr: acta.unr,
        unr_actividad_nombre: acta.unr_actividad_nombre,
        producto_unr: acta.producto_unr,
        unr_largo: acta.unr_largo,
        unr_ancho: acta.unr_ancho,
        unr_area: acta.unr_area,
        unr_ocup: acta.unr_ocup,
        unr_vacias: acta.unr_vacias,

        // Actualización de datos
        direccion_predio: acta.direccion_predio,
        estrato_predio: acta.estrato_predio,
        localidad_predio: acta.localidad_predio,
        actualizacion_nombre_suscriptor: acta.actualizacion_nombre_suscriptor,
        actualizacion_numero_meses_financiar: acta.actualizacion_numero_meses_financiar,

        // Características del predio
        abandonado: acta.abandonado,
        suscriptor_inactivo: acta.suscriptor_inactivo,
        demolido: acta.demolido,
        causa_demolicion: acta.causa_demolicion,
        en_construccion: acta.en_construccion,
        en_remodelacion: acta.en_remodelacion,
        inexistente: acta.inexistente,
        inhabitable_o_en_ruinas: acta.inhabitable_o_en_ruinas,
        lote: acta.lote,
        minimas_condiciones: acta.minimas_condiciones,
        programa_del_icbf: acta.programa_del_icbf,
        descuento_puerta_a_puerta: acta.descuento_puerta_a_puerta,
        se_cierra_pqr_como_no_atendido: acta.se_cierra_pqr_como_no_atendido,
        areas_comunes: acta.areas_comunes,
        predio_que_se_observa_vacio: acta.predio_que_se_observa_vacio,
        predio_se_observa_ocupado: acta.predio_se_observa_ocupado,
        cuenta_suscriptor_activo: acta.cuenta_suscriptor_activo,

        // Tipos de residuos
        escombros: acta.escombros,
        especiales: acta.especiales,
        para_relleno: acta.para_relleno,
        para_escombrera: acta.para_escombrera,
        enlonado: acta.enlonado,
        a_granel: acta.a_granel,
        tipo_recoleccion: acta.tipo_recoleccion,

        // Información de recipientes
        descripcion_recipiente: acta.descripcion_recipiente,
        recipiente_largo: acta.recipiente_largo,
        recipiente_ancho: acta.recipiente_ancho,
        recipiente_alto: acta.recipiente_alto,
        recipiente_cantidad: acta.recipiente_cantidad,
        recipiente_m3: acta.recipiente_m3,
        m3_gratis: acta.m3_gratis,
        dos_m3_gratis: acta.dos_m3_gratis,
        valor_m3: acta.valor_m3,
        cancelado: acta.cancelado,

        // Observaciones
        observaciones: acta.observaciones,

        // Representante usuario
        representante_nombre: acta.representante_nombre,
        representante_cedula: acta.representante_cedula,
        representante_telefono_celular: acta.representante_telefono_celular,
        representante_firma: acta.representante_firma,
        anotaciones: acta.anotaciones,
        sin_firma: acta.sin_firma,

        // Representante ciudad limpia
        ciudad_limpia_nombre: acta.ciudad_limpia_nombre,
        ciudad_limpia_codigo: acta.ciudad_limpia_codigo,
        ciudad_limpia_firma: acta.ciudad_limpia_firma,
        ciudad_limpia_telefono: acta.ciudad_limpia_telefono,

        // Imágenes específicas del acta adicional
        images: acta.images || []
      }));


      // Generar y abrir PDF
      // Usar los datos actualizados del caso
      const result = await openActaVisitaPDF(actaData, casoData, actasAdicionales);

      Swal.close();

      if (!result.success) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo generar el PDF. Por favor intenta nuevamente.',
          confirmButtonColor: '#dc3545'
        });
      }
    } catch (error) {
      console.error('Error al generar PDF:', error);
      Swal.close();
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Ocurrió un error al generar el PDF. Por favor intenta nuevamente.',
        confirmButtonColor: '#dc3545'
      });
    }
  };

  // Función para buscar contacto por cuenta_suscriptor
  const buscarContactoPorCuenta = async (cuenta_suscriptor) => {
    if (!cuenta_suscriptor || cuenta_suscriptor.trim() === '') {
      setContactSearchError('');
      actualizarActaActual({
        cuenta_suscriptor: cuenta_suscriptor, // Mantener el valor buscado
        direccion: '',
        nombre_cliente: '',
        direccion_informe: '',
        cuenta_energia: ''
      });
      return;
    }

    setSearchingContact(true);
    setContactSearchError('');

    try {
      const response = await getContactsFilter({
        changeUrl: `?search=${cuenta_suscriptor}`
      });


      if (response && response.data && response.data.results && response.data.results.length > 0) {
        const contactData = response.data.results[0];
        const address = contactData.address && contactData.address.length > 0 ? contactData.address[0].address : '';
        const updateData = {
          cuenta_suscriptor: cuenta_suscriptor, // Mantener el valor buscado
          direccion: address,
          nombre_cliente: contactData.full_name || '',
          direccion_informe: address, // Usar la misma dirección para direccion_informe
          cuenta_energia: contactData.cuenta_enel || ''
        };
        actualizarActaActual(updateData);
        setContactSearchError('');
      } else {
        setContactSearchError('La cuenta contrato no existe en la base de datos');
        actualizarActaActual({
          cuenta_suscriptor: cuenta_suscriptor, // Mantener el valor buscado
          direccion: '',
          nombre_cliente: '',
          direccion_informe: '',
          cuenta_energia: ''
        });
      }
    } catch (error) {
      console.error('Error al buscar contacto:', error);
      setContactSearchError('Error al buscar la cuenta contrato');
      actualizarActaActual({
        direccion: '',
        nombre_cliente: '',
        direccion_informe: '',
        cuenta_energia: ''
      });
    } finally {
      setSearchingContact(false);
    }
  };

  // Funciones para gestionar múltiples actas
  const agregarNuevaActa = () => {
    // Validar que no hay cambios pendientes en acta principal
    if (actaPrincipalTieneCambios) {
      Swal.fire({
        icon: 'warning',
        title: 'Cambios Pendientes',
        text: 'Debe guardar los cambios del Acta Principal antes de crear una nueva acta adicional',
        confirmButtonColor: '#dc3545'
      });
      return;
    }

    // Validar que no hay otra acta adicional en proceso
    if (actaAdicionalEnProceso) {
      Swal.fire({
        icon: 'warning',
        title: 'Acta en Proceso',
        text: 'Ya hay una acta adicional en proceso de creación. Complete el guardado antes de crear otra',
        confirmButtonColor: '#dc3545'
      });
      return;
    }

    // Guardar cambios del acta actual antes de crear una nueva
    const actasActualizadas = [...actas];
    actasActualizadas[actaActualIndex] = { ...actaVisita };
    setActas(actasActualizadas);

    const nuevaActa = crearActaVacia(nextActaId, false);

    // Heredar datos adicionales del acta principal para actas adicionales
    const actaPrincipal = actasActualizadas.find(acta => acta.esPrincipal);
    if (actaPrincipal) {
      nuevaActa.direccion_predio = actaPrincipal.direccion_predio;
      nuevaActa.estrato_predio = actaPrincipal.estrato_predio;
      nuevaActa.localidad_predio = actaPrincipal.localidad_predio;
      nuevaActa.representante_nombre = actaPrincipal.representante_nombre;
      nuevaActa.representante_cedula = actaPrincipal.representante_cedula;
      nuevaActa.representante_telefono_celular = actaPrincipal.representante_telefono_celular;
      nuevaActa.representante_firma = actaPrincipal.representante_firma; // Heredar firma del representante
      nuevaActa.ciudad_limpia_nombre = actaPrincipal.ciudad_limpia_nombre;
      nuevaActa.ciudad_limpia_telefono = actaPrincipal.ciudad_limpia_telefono;
      nuevaActa.ciudad_limpia_codigo = actaPrincipal.ciudad_limpia_codigo;
      nuevaActa.ciudad_limpia_firma = actaPrincipal.ciudad_limpia_firma; // Heredar firma de ciudad limpia
      nuevaActa.sin_firma = actaPrincipal.sin_firma;

      // Heredar también fecha y hora
      nuevaActa.fecha_visita = actaPrincipal.fecha_visita;
      nuevaActa.fecha_informe = actaPrincipal.fecha_informe;
      nuevaActa.hora_visita = actaPrincipal.hora_visita;
      nuevaActa.dia_visita = actaPrincipal.dia_visita;
      nuevaActa.mes_visita = actaPrincipal.mes_visita;
      nuevaActa.año_visita = actaPrincipal.año_visita;
      nuevaActa.ciudad_visita = actaPrincipal.ciudad_visita;
    }

    const nuevasActas = [...actasActualizadas, nuevaActa];
    setActas(nuevasActas);
    setNextActaId(prev => prev + 1);
    setActaActualIndex(nuevasActas.length - 1);
    setActaVisita(nuevaActa);

    // Marcar que hay una acta adicional en proceso
    setActaAdicionalEnProceso(true);
  };

  const eliminarActa = async (index) => {
    const actaAEliminar = actas[index];

    if (actaAEliminar?.esPrincipal) {
      Swal.fire({
        icon: 'warning',
        title: 'No se puede eliminar',
        text: 'No se puede eliminar el acta principal',
        confirmButtonColor: '#dc3545'
      });
      return;
    }

    // Determinar si es un acta nueva o existente
    const esActaNueva = actaAEliminar.esNueva || !actaAEliminar.servidorId;
    const tipoEliminacion = esActaNueva ? 'de la vista' : 'del sistema';

    Swal.fire({
      title: '¿Estás seguro?',
      text: `Esta acción eliminará el acta adicional ${tipoEliminacion} y no se puede deshacer`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          // Si el acta existe en el servidor, eliminarla del backend
          if (!esActaNueva && actaAEliminar.servidorId) {
            // Mostrar loading
            Swal.fire({
              title: 'Eliminando acta...',
              text: 'Por favor espere',
              allowOutsideClick: false,
              didOpen: () => {
                Swal.showLoading();
              }
            });

            await deleteActaAdicional(actaAEliminar.servidorId);
          }

          // Eliminar del array local
          const nuevasActas = actas.filter((_, i) => i !== index);
          setActas(nuevasActas);

          // Si eliminamos el acta actual, cambiar al acta principal
          if (index === actaActualIndex) {
            setActaActualIndex(0);
            setActaVisita(nuevasActas[0]);
          } else if (index < actaActualIndex) {
            setActaActualIndex(prev => prev - 1);
          }

          // Mostrar mensaje de éxito
          Swal.fire({
            icon: 'success',
            title: 'Acta Eliminada',
            text: `El acta adicional ha sido eliminada ${tipoEliminacion} correctamente`,
            confirmButtonColor: '#2fa4e7'
          });

        } catch (error) {
          console.error('Error al eliminar acta:', error);
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'No se pudo eliminar el acta adicional. Por favor intente nuevamente.',
            confirmButtonColor: '#2fa4e7'
          });
        }
      }
    });
  };

  const cambiarActa = (index) => {
    if (index >= 0 && index < actas.length) {
      // Guardar cambios del acta actual antes de cambiar
      const actasActualizadas = [...actas];
      actasActualizadas[actaActualIndex] = { ...actaVisita };
      setActas(actasActualizadas);

      // Cambiar al acta seleccionada usando los datos actualizados
      setActaActualIndex(index);
      setActaVisita(actasActualizadas[index]);
    }
  };

  const actualizarActaActual = (nuevosValores) => {

    const actaActualizada = { ...actaVisita, ...nuevosValores };

    // Si se está actualizando fecha_informe, sincronizar todos los campos de fecha
    if (nuevosValores.fecha_informe) {
      const fechaInforme = nuevosValores.fecha_informe;

      if (fechaInforme) {
        // Evitar problemas de zona horaria construyendo la fecha directamente
        const [año, mes, dia] = fechaInforme.split('-');
        if (año && mes && dia) {
          const fecha = new Date(año, mes - 1, dia); // mes-1 porque Date usa 0-11

          if (!isNaN(fecha.getTime())) {
            const diaVisita = fecha.getDate();
            const mesVisita = fecha.toLocaleDateString('es-ES', { month: 'long' });
            const añoVisita = fecha.getFullYear();

            // Actualizar todos los campos de fecha basados en fecha_informe
            actaActualizada.fecha_visita = fechaInforme; // fecha_visita siempre refleja fecha_informe
            actaActualizada.dia_visita = diaVisita.toString();
            actaActualizada.mes_visita = mesVisita;
            actaActualizada.año_visita = añoVisita.toString();
          }
        }
      }
    }
    // Si se actualiza fecha_visita directamente, también actualizar fecha_informe
    else if (nuevosValores.fecha_visita) {
      const fechaVisita = nuevosValores.fecha_visita;

      if (fechaVisita) {
        // Evitar problemas de zona horaria construyendo la fecha directamente
        const [año, mes, dia] = fechaVisita.split('-');
        if (año && mes && dia) {
          const fecha = new Date(año, mes - 1, dia); // mes-1 porque Date usa 0-11

          if (!isNaN(fecha.getTime())) {
            const diaVisita = fecha.getDate();
            const mesVisita = fecha.toLocaleDateString('es-ES', { month: 'long' });
            const añoVisita = fecha.getFullYear();

            // Sincronizar fecha_informe con fecha_visita y campos separados
            actaActualizada.fecha_informe = fechaVisita;
            actaActualizada.dia_visita = diaVisita.toString();
            actaActualizada.mes_visita = mesVisita;
            actaActualizada.año_visita = añoVisita.toString();
          }
        }
      }
    }

    setActaVisita(actaActualizada);

    // Detectar cambios en el acta principal
    if (actaActualizada.esPrincipal) {
      setActaPrincipalTieneCambios(true);
    } else if (!actaActualizada.esPrincipal && actaActualizada.esNueva) {
      // Para actas adicionales nuevas, marcar que hay una en proceso
      setActaAdicionalEnProceso(true);
    }

    // Actualizar en el array de actas
    const actasActualizadas = [...actas];
    actasActualizadas[actaActualIndex] = actaActualizada;
    setActas(actasActualizadas);

  };

  // Prellenar datos del caso y del informe existente
  useEffect(() => {
    if (caso) {
      const informe = caso.informe || {};
      const cliente = caso.cliente?.[0] || {};
      const agente = caso.contacto?.[0] || caso.agente || {};
      // Obtener fecha actual para campos de fecha si no existen
      const fechaActual = new Date();
      const dia = fechaActual.getDate();
      const mes = fechaActual.toLocaleDateString('es-ES', { month: 'long' });
      const año = fechaActual.getFullYear();
      const fechaHoyStr = fechaActual.toISOString().split('T')[0];

      setActaVisita(prev => ({
        ...prev,
        // Campos de fecha - fecha_informe es la fuente de verdad (fallback a hoy si vacío)
        fecha_informe: convertirFechaParaInput(informe.fecha_informe || informe.fecha_visita || fechaHoyStr) || prev.fecha_informe || fechaHoyStr,
        fecha_visita: convertirFechaParaInput(informe.fecha_informe || informe.fecha_visita || fechaHoyStr) || prev.fecha_visita || fechaHoyStr, // fecha_visita refleja fecha_informe
        hora_visita: informe.hora_visita || prev.hora_visita,
        dia_visita: informe.dia_visita || dia.toString(),
        mes_visita: informe.mes_visita || mes,
        año_visita: informe.año_visita || año.toString(),
        ciudad_visita: informe.ciudad_visita || 'Bogotá',
        archivo_no: informe.archivo_no || '',
        no_pqrs: informe.no_de_pqrs || '',
        cuenta_suscriptor: informe.cuenta_suscriptor || '',
        cuenta_energia: informe.cuenta_energia || '',
        nombre_cliente: informe.nombre_cliente || cliente.full_name || '',
        direccion: informe.direccion || cliente.address?.[0]?.address || '',

        // Nuevas estructuras de datos dinámicas
        unidades_residenciales_detalle: informe.unidades_residenciales_detalle ?
          (typeof informe.unidades_residenciales_detalle === 'string' ?
            JSON.parse(informe.unidades_residenciales_detalle) :
            informe.unidades_residenciales_detalle) : [],
        producto_nombre: informe.producto_nombre || '',
        producto_detalle: informe.producto_detalle ?
          (typeof informe.producto_detalle === 'string' ?
            JSON.parse(informe.producto_detalle) :
            informe.producto_detalle) : [],
        unidades_no_residenciales_detalle: informe.unidades_no_residenciales_detalle ?
          (typeof informe.unidades_no_residenciales_detalle === 'string' ?
            JSON.parse(informe.unidades_no_residenciales_detalle) :
            informe.unidades_no_residenciales_detalle) : [],
        recipientes_detalle: (() => {
          let recipientes = [];
          if (informe.recipientes_detalle) {
            if (typeof informe.recipientes_detalle === 'string') {
              try {
                const parsed = JSON.parse(informe.recipientes_detalle);
                recipientes = parsed.map(item => ({
                  descripcion: item.descripcion || '',
                  largo: item.largo || '',
                  ancho: item.ancho || '',
                  alto: item.alto || '',
                  cantidad: item.cantidad || '',
                  m3: item.m3 || ''
                }));
              } catch (e) {
                console.error('Error parsing recipientes_detalle:', e);
                recipientes = [];
              }
            } else {
              recipientes = informe.recipientes_detalle;
            }
          }
          return recipientes;
        })(),

        // Información UR
        ur_piso: informe.ur_piso || '',
        ur: informe.ur || '',
        ur_ocupadas: informe.ur_ocupadas || '',
        ur_vacias: informe.ur_vacias || '',

        // Información Producto
        producto_piso: informe.producto_piso || '',
        producto_ur: informe.producto_ur || '',
        producto_ocupadas: informe.producto_ocupadas || '',
        producto_vacias: informe.producto_vacias || '',

        // Información UNR
        unr_piso: informe.unr_piso || '',
        unr: informe.unr || '',
        unr_actividad_nombre: informe.unr_actividad_nombre || '',
        producto_unr: informe.producto_unr || '',
        unr_largo: informe.unr_largo || '',
        unr_ancho: informe.unr_ancho || '',
        unr_area: informe.unr_area || '',
        unr_ocup: informe.unr_ocup || '',
        unr_vacias: informe.unr_vacias || '',

        // Actualización de datos
        direccion_predio: informe.direccion_predio || '',
        estrato_predio: informe.estrato_predio || '',
        localidad_predio: informe.localidad_predio || '',
        actualizacion_nombre_suscriptor: informe.actualizacion_nombre_suscriptor || '',
        actualizacion_numero_meses_financiar: informe.actualizacion_numero_meses_financiar || '',

        // Características del predio
        abandonado: informe.abandonado || false,
        suscriptor_inactivo: informe.suscriptor_inactivo || false,
        demolido: informe.demolido || false,
        causa_demolicion: informe.causa_demolicion || '',
        en_construccion: informe.en_construccion || false,
        en_remodelacion: informe.en_remodelacion || false,
        inexistente: informe.inexistente || false,
        inhabitable_o_en_ruinas: informe.inhabitable_o_en_ruinas || false,
        lote: informe.lote || false,
        minimas_condiciones: informe.minimas_condiciones || false,
        programa_del_icbf: informe.programa_del_icbf || false,
        descuento_puerta_a_puerta: informe.descuento_puerta_a_puerta || false,
        se_cierra_pqr_como_no_atendido: informe.se_cierra_pqr_como_no_atendido || false,
        areas_comunes: informe.areas_comunes || false,
        predio_que_se_observa_vacio: informe.predio_que_se_observa_vacio || false,
        predio_se_observa_ocupado: informe.predio_se_observa_ocupado || false,
        cuenta_suscriptor_activo: informe.cuenta_suscriptor_activo || false,
        suscriptor_activo_detalles: informe.suscriptor_activo_detalles || '',

        // Tipos de residuos
        escombros: informe.escombros || false,
        especiales: informe.especiales || false,
        para_relleno: informe.para_relleno || false,
        para_escombrera: informe.para_escombrera || false,
        enlonado: informe.enlonado || false,
        a_granel: informe.a_granel || false,
        tarifa_lodo: informe.tarifa_lodo || false,
        tipo_recoleccion: informe.tipo_recoleccion || '',

        // Información de recipientes
        descripcion_recipiente: informe.descripcion_recipiente || '',
        recipiente_largo: informe.recipiente_largo || '',
        recipiente_ancho: informe.recipiente_ancho || '',
        recipiente_alto: informe.recipiente_alto || '',
        recipiente_cantidad: informe.recipiente_cantidad || '',
        recipiente_m3: informe.recipiente_m3 || '',
        m3_gratis: informe.m3_gratis || false,
        dos_m3_gratis: (informe.dos_m3_gratis ?? informe['2m3_gratis']) || false,
        cancelado: informe.cancelado || false,
        valor_m3: informe.valor_m3 || '',

        // Observaciones
        observaciones: informe.observaciones || '',

        // Representantes
        representante_nombre: caso.metadata?.carga_radicados?.nombre || informe.representante_nombre || '',
        representante_cedula: caso.metadata?.carga_radicados?.cedula || informe.representante_cedula || '',
        representante_telefono_celular: caso.metadata?.carga_radicados?.celular || informe.representante_telefono_celular || '',
        representante_firma: informe.representante_firma || null,
        anotaciones: informe.anotaciones || '',
        sin_firma: informe.sin_firma || false,

        ciudad_limpia_nombre: informe.ciudad_limpia_nombre || agente.full_name || '',
        ciudad_limpia_telefono: informe.ciudad_limpia_telefono || '',
        ciudad_limpia_codigo: informe.ciudad_limpia_codigo || agente.codigo_visitador || '',
        ciudad_limpia_firma: informe.ciudad_limpia_firma || null
      }));

      // Sincronizar campos separados si existe fecha_informe
      setActaVisita(prevActa => {
        const fechaInforme = prevActa.fecha_informe;
        if (fechaInforme) {
          // Evitar problemas de zona horaria construyendo la fecha directamente
          const [año, mes, dia] = fechaInforme.split('-');
          if (año && mes && dia) {
            const fecha = new Date(año, mes - 1, dia); // mes-1 porque Date usa 0-11
            if (!isNaN(fecha.getTime())) {
              const diaNum = fecha.getDate();
              const mesNombre = fecha.toLocaleDateString('es-ES', { month: 'long' });
              const añoNum = fecha.getFullYear();

              return {
                ...prevActa,
                dia_visita: diaNum.toString(),
                mes_visita: mesNombre,
                año_visita: añoNum.toString(),
                fecha_visita: fechaInforme // Asegurar que fecha_visita refleje fecha_informe
              };
            }
          }
        }
        return prevActa;
      });

      // Buscar tarifa del cliente si ya tenemos las tarifas cargadas
      if (tarifas.length > 0) {
        buscarTarifaCliente(tarifas);
      }

    }
  }, [caso, tarifas]);

  // Cargar actas adicionales existentes desde caso.informes_adicionales
  useEffect(() => {
    if (caso?.informes_adicionales && Array.isArray(caso.informes_adicionales) && caso.informes_adicionales.length > 0) {
      try {
        // Convertir informes adicionales a formato de actas
        const actasAdicionalesData = caso.informes_adicionales.map((informe, index) => {
          const actaAdicional = {
            id: informe.id || (index + 2), // ID único, empezando desde 2
            esPrincipal: false,
            titulo: `Acta Adicional ${informe.id || (index + 2)}`,
            // Control de estado para actas adicionales existentes
            esNueva: false, // Las actas cargadas desde el servidor no son nuevas
            servidorId: informe.id, // ID del servidor

            // Mapear todos los campos del informe (fallback a hoy si vacío)
            fecha_informe: convertirFechaParaInput(informe.fecha_informe || informe.fecha_visita || caso.informe?.fecha_visita || new Date().toISOString().split('T')[0]),
            fecha_visita: convertirFechaParaInput(informe.fecha_informe || informe.fecha_visita || caso.informe?.fecha_visita || new Date().toISOString().split('T')[0]),
            hora_visita: informe.hora_visita || caso.informe?.hora_visita || '',
            ciudad_visita: informe.ciudad_visita || caso.informe?.ciudad_visita || 'Bogotá',
            dia_visita: informe.dia_visita || caso.informe?.dia_visita || '',
            mes_visita: informe.mes_visita || caso.informe?.mes_visita || '',
            año_visita: informe.año_visita || caso.informe?.año_visita || '',
            archivo_no: informe.archivo_no || '',
            no_pqrs: informe.no_de_pqrs || '',
            cuenta_suscriptor: informe.cuenta_suscriptor || '',
            cuenta_energia: informe.cuenta_energia || '',
            nombre_cliente: informe.nombre_cliente || '',
            direccion: informe.direccion || '',

            // Estructuras de datos dinámicas
            unidades_residenciales_detalle: Array.isArray(informe.unidades_residenciales_detalle)
              ? informe.unidades_residenciales_detalle
              : (typeof informe.unidades_residenciales_detalle === 'string'
                ? JSON.parse(informe.unidades_residenciales_detalle || '[]')
                : []),
            producto_nombre: informe.producto_nombre || '',
            producto_detalle: Array.isArray(informe.producto_detalle)
              ? informe.producto_detalle
              : (typeof informe.producto_detalle === 'string'
                ? JSON.parse(informe.producto_detalle || '[]')
                : []),
            unidades_no_residenciales_detalle: Array.isArray(informe.unidades_no_residenciales_detalle)
              ? informe.unidades_no_residenciales_detalle
              : (typeof informe.unidades_no_residenciales_detalle === 'string'
                ? JSON.parse(informe.unidades_no_residenciales_detalle || '[]')
                : []),
            recipientes_detalle: (() => {
              let recipientes = [];
              if (Array.isArray(informe.recipientes_detalle)) {
                recipientes = informe.recipientes_detalle;
              } else if (typeof informe.recipientes_detalle === 'string') {
                try {
                  const parsed = JSON.parse(informe.recipientes_detalle || '[]');
                  recipientes = parsed.map(item => ({
                    descripcion: item.descripcion || '',
                    largo: item.largo || '',
                    ancho: item.ancho || '',
                    alto: item.alto || '',
                    cantidad: item.cantidad || '',
                    m3: item.m3 || ''
                  }));
                } catch (e) {
                  console.error('Error parsing recipientes_detalle en informes adicionales:', e);
                  recipientes = [];
                }
              }
              return recipientes;
            })(),

            // Campos legacy
            ur_piso: informe.ur_piso || '',
            ur: informe.ur || '',
            ur_ocupadas: informe.ur_ocupadas || '',
            ur_vacias: informe.ur_vacias || '',
            producto_piso: informe.producto_piso || '',
            producto_ur: informe.producto_ur || '',
            producto_ocupadas: informe.producto_ocupadas || '',
            producto_vacias: informe.producto_vacias || '',
            unr_piso: informe.unr_piso || '',
            unr: informe.unr || '',
            unr_actividad_nombre: informe.unr_actividad_nombre || '',
            producto_unr: informe.producto_unr || '',
            unr_largo: informe.unr_largo || '',
            unr_ancho: informe.unr_ancho || '',
            unr_area: informe.unr_area || '',
            unr_ocup: informe.unr_ocup || '',
            unr_vacias: informe.unr_vacias || '',

            // Actualización de datos
            direccion_predio: informe.direccion_predio || '',
            estrato_predio: informe.estrato_predio || '',
            localidad_predio: informe.localidad_predio || '',
            actualizacion_nombre_suscriptor: informe.actualizacion_nombre_suscriptor || '',
            actualizacion_numero_meses_financiar: informe.actualizacion_numero_meses_financiar || '',

            // Características del predio
            abandonado: informe.abandonado || false,
            suscriptor_inactivo: informe.suscriptor_inactivo || false,
            demolido: informe.demolido || false,
            causa_demolicion: informe.causa_demolicion || '',
            en_construccion: informe.en_construccion || false,
            en_remodelacion: informe.en_remodelacion || false,
            inexistente: informe.inexistente || false,
            inhabitable_o_en_ruinas: informe.inhabitable_o_en_ruinas || false,
            lote: informe.lote || false,
            minimas_condiciones: informe.minimas_condiciones || false,
            programa_del_icbf: informe.programa_del_icbf || false,
            descuento_puerta_a_puerta: informe.descuento_puerta_a_puerta || false,
            se_cierra_pqr_como_no_atendido: informe.se_cierra_pqr_como_no_atendido || false,
            areas_comunes: informe.areas_comunes || false,
            predio_que_se_observa_vacio: informe.predio_que_se_observa_vacio || false,
            predio_se_observa_ocupado: informe.predio_se_observa_ocupado || false,
            cuenta_suscriptor_activo: informe.cuenta_suscriptor_activo || false,

            // Tipos de residuos
            escombros: informe.escombros || false,
            especiales: informe.especiales || false,
            para_relleno: informe.para_relleno || false,
            para_escombrera: informe.para_escombrera || false,
            enlonado: informe.enlonado || false,
            a_granel: informe.a_granel || false,
            tarifa_lodo: informe.tarifa_lodo || false,
            tipo_recoleccion: informe.tipo_recoleccion || '',

            // Información de recipientes
            descripcion_recipiente: informe.descripcion_recipiente || '',
            recipiente_largo: informe.recipiente_largo || '',
            recipiente_ancho: informe.recipiente_ancho || '',
            recipiente_alto: informe.recipiente_alto || '',
            recipiente_cantidad: informe.recipiente_cantidad || '',
            recipiente_m3: informe.recipiente_m3 || '',
            m3_gratis: informe.m3_gratis || false,
            dos_m3_gratis: (informe.dos_m3_gratis ?? informe['2m3_gratis']) || false,
            valor_m3: informe.valor_m3 || '',
            cancelado: informe.cancelado || false,

            // Observaciones
            observaciones: informe.observaciones || '',

            // Representante usuario
            representante_nombre: informe.representante_nombre || '',
            representante_cedula: informe.representante_cedula || '',
            representante_telefono_celular: informe.representante_telefono_celular || '',
            representante_firma: informe.representante_firma || null,
            anotaciones: informe.anotaciones || '',
            sin_firma: informe.sin_firma || false,

            // Representante ciudad limpia
            ciudad_limpia_nombre: informe.ciudad_limpia_nombre || '',
            ciudad_limpia_telefono: informe.ciudad_limpia_telefono || '',
            ciudad_limpia_codigo: informe.ciudad_limpia_codigo || '',
            ciudad_limpia_firma: informe.ciudad_limpia_firma || null,

            // Imágenes específicas del informe adicional
            images: informe.images || []
          };

          return actaAdicional;
        });

        if (actasAdicionalesData.length > 0) {
          // Combinar acta principal con actas adicionales
          const actaPrincipal = actas.find(acta => acta.esPrincipal) || crearActaVacia(1, true);
          const todasLasActas = [actaPrincipal, ...actasAdicionalesData];

          setActas(todasLasActas);
          setNextActaId(Math.max(...actasAdicionalesData.map(acta => acta.id)) + 1);
        }
      } catch (error) {
        console.error('Error al cargar actas adicionales:', error);
      }
    }
  }, [caso?.informes_adicionales]);





  // Función para encontrar el primer paso disponible
  const findFirstAvailableStep = () => {
    for (let i = 1; i <= totalSteps; i++) {
      if (!isStepBlocked(i)) {
        return i;
      }
    }
    return 1; // Fallback al primer paso
  };

  // Verificar si el paso actual está bloqueado y redirigir si es necesario
  useEffect(() => {
    if (caso?.tipo_caso && isStepBlocked(currentStep)) {
      const nextValidStep = findFirstAvailableStep();
      setCurrentStep(nextValidStep);
    }
  }, [caso?.tipo_caso, currentStep]);

  // Función para formatear valor como moneda para visualización
  const formatCurrencyDisplay = (value) => {
    if (!value || value === '') return '';
    const number = parseFloat(value);
    if (isNaN(number)) return value;
    return number.toLocaleString('es-CO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Función para manejar el formateo al perder el foco
  const handleCurrencyBlur = (e) => {
    const { name, value } = e.target;
    if (value && !isNaN(value)) {
      const formattedValue = formatCurrencyDisplay(value);
      setActaVisita(prev => ({
        ...prev,
        [name]: formattedValue
      }));
    }
  };

  // Función para manejar el foco en campos de moneda
  const handleCurrencyFocus = (e) => {
    const { name, value } = e.target;
    if (value) {
      // Extraer solo el número del valor formateado
      const numericValue = value.replace(/[^0-9.,]/g, '').replace(',', '.');
      setActaVisita(prev => ({
        ...prev,
        [name]: numericValue
      }));
    }
  };

  // Manejadores de cambio
  const handleActaChange = (e) => {
    const { name, value, type, checked } = e.target;

    const newState = {
      [name]: type === 'checkbox' ? checked : value
    };

    // Validaciones automáticas para tipos de residuos
    if (type === 'checkbox') {
      // Al activar sin_firma, limpiar datos del representante del usuario
      if (name === 'sin_firma' && checked) {
        newState.representante_nombre = '';
        newState.representante_cedula = '';
        newState.representante_firma = null;
      }

      // Validaciones para escombros
      if (name === 'escombros') {
        if (checked) {
          // Si selecciona escombros: auto-seleccionar escombrera y bloquear especiales
          newState.para_escombrera = true;
          newState.especiales = false;
          newState.para_relleno = false;
        } else {
          // Si deselecciona escombros: deseleccionar escombrera también
          newState.para_escombrera = false;
        }
      }

      // Validaciones para especiales
      if (name === 'especiales') {
        if (checked) {
          // Si selecciona especiales: auto-seleccionar para_relleno, bloquear escombros y limpiar m3_gratis
          newState.para_relleno = true;
          newState.escombros = false;
          newState.para_escombrera = false;
          newState.m3_gratis = false; // Limpiar m3_gratis cuando se selecciona especiales
          newState.dos_m3_gratis = false; // Limpiar 2m3_gratis cuando se selecciona especiales
        } else {
          // Si deselecciona especiales: deseleccionar para_relleno también
          newState.para_relleno = false;
        }
        // Activar cálculo automático cuando se cambia "especiales"
        setUsarCalculoValorM3(true);
      }

      // Validaciones para para_escombrera
      if (name === 'para_escombrera') {
        if (checked) {
          // Si selecciona para_escombrera: auto-seleccionar escombros
          newState.escombros = true;
          newState.especiales = false;
          newState.para_relleno = false;
        } else {
          // Si deselecciona para_escombrera: deseleccionar escombros también
          newState.escombros = false;
        }
      }

      // Validaciones para para_relleno
      if (name === 'para_relleno') {
        if (checked) {
          // Si selecciona para_relleno: auto-seleccionar especiales
          newState.especiales = true;
          newState.escombros = false;
          newState.para_escombrera = false;
        } else {
          // Si deselecciona para_relleno: deseleccionar especiales también
          newState.especiales = false;
        }
      }

      // Activar cálculo automático cuando cambia m3_gratis
      if (name === 'm3_gratis') {
        if (checked) {
          newState.dos_m3_gratis = false;
        }
        setUsarCalculoValorM3(true);
      }
      // Activar cálculo automático cuando cambia dos_m3_gratis
      if (name === 'dos_m3_gratis') {
        if (checked) {
          newState.m3_gratis = false;
        }
        setUsarCalculoValorM3(true);
      }

      // Reseteo de recipientes y valor_m3 al marcar cancelado
      if (name === 'cancelado' && checked) {
        newState.descripcion_recipiente = '';
        newState.recipiente_largo = '';
        newState.recipiente_ancho = '';
        newState.recipiente_alto = '';
        newState.recipiente_cantidad = '';
        newState.recipiente_m3 = '';
        newState.m3_gratis = false;
        newState.dos_m3_gratis = false;
        newState.valor_m3 = '';
        newState.recipientes_detalle = [];
        // Además, limpiar tipos de residuos y tipo de recolección
        newState.escombros = false;
        newState.especiales = false;
        newState.para_relleno = false;
        newState.para_escombrera = false;
        newState.enlonado = false;
        newState.a_granel = false;
        newState.tarifa_lodo = false;
        newState.tipo_recoleccion = '';
        setUsarCalculoValorM3(false);
        setOcultarChecksM3(false);
        setMotivoRestriccionM3(null);
        setPermitir1m3(true);
        setPermitir2m3(true);
      }
    }

    actualizarActaActual(newState);

    // Removida la búsqueda automática de cuenta_suscriptor
    // Ahora se ejecutará solo con el botón de búsqueda
  };

  // (Edición manual removida por requerimiento)

  // Nueva función para manejar la búsqueda manual de contacto
  const handleBuscarContacto = () => {
    if (actaVisita.cuenta_suscriptor && actaVisita.cuenta_suscriptor.trim() !== '') {
      buscarContactoPorCuenta(actaVisita.cuenta_suscriptor);
    } else {
      Swal.fire({
        icon: 'warning',
        title: 'Campo requerido',
        text: 'Por favor ingrese un número de cuenta suscriptor para buscar',
        confirmButtonColor: '#2fa4e7'
      });
    }
  };

  // Función para manejar Enter en el campo de cuenta_suscriptor
  const handleCuentaSuscriptorKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBuscarContacto();
    }
  };

  // Manejador específico para campos de moneda
  const handleCurrencyChange = (e) => {
    const { name, value } = e.target;
    // Permitir solo números y punto decimal
    const cleanValue = value.replace(/[^\d.]/g, '');
    actualizarActaActual({ [name]: cleanValue });
  };

  // Calcular área automáticamente cuando cambian dimensiones UNR
  const handleDimensionChange = (e) => {
    const { name, value } = e.target;
    const newState = { [name]: value };

    // Calcular área UNR automáticamente, dejando vacío si faltan datos
    if (name === 'unr_largo' || name === 'unr_ancho') {
      const largoStr = name === 'unr_largo' ? value : actaVisita.unr_largo;
      const anchoStr = name === 'unr_ancho' ? value : actaVisita.unr_ancho;
      const hasLargo = largoStr !== '' && largoStr !== null && largoStr !== undefined;
      const hasAncho = anchoStr !== '' && anchoStr !== null && anchoStr !== undefined;
      if (hasLargo && hasAncho) {
        const largo = parseFloat(largoStr);
        const ancho = parseFloat(anchoStr);
        if (!isNaN(largo) && !isNaN(ancho)) {
          newState.unr_area = (largo * ancho).toString();
        } else {
          newState.unr_area = '';
        }
      } else {
        newState.unr_area = '';
      }
    }

    // Calcular volumen de recipiente automáticamente, dejando vacío si faltan datos
    if (name === 'recipiente_largo' || name === 'recipiente_ancho' || name === 'recipiente_alto' || name === 'recipiente_cantidad') {
      const largoStr = name === 'recipiente_largo' ? value : actaVisita.recipiente_largo;
      const anchoStr = name === 'recipiente_ancho' ? value : actaVisita.recipiente_ancho;
      const altoStr = name === 'recipiente_alto' ? value : actaVisita.recipiente_alto;
      const cantidadStr = name === 'recipiente_cantidad' ? value : actaVisita.recipiente_cantidad;
      const allPresent = [largoStr, anchoStr, altoStr, cantidadStr].every(v => v !== '' && v !== null && v !== undefined);
      if (allPresent) {
        const largo = parseFloat(largoStr);
        const ancho = parseFloat(anchoStr);
        const alto = parseFloat(altoStr);
        const cantidad = parseFloat(cantidadStr);
        if (![largo, ancho, alto, cantidad].some(n => isNaN(n))) {
          const volumenTotal = largo * ancho * alto * cantidad;
          newState.recipiente_m3 = volumenTotal.toFixed(2);
        } else {
          newState.recipiente_m3 = '';
        }
      } else {
        newState.recipiente_m3 = '';
      }
    }

    actualizarActaActual(newState);
  };

  // Funciones para manejar tablas dinámicas
  const addRowToTable = (tableType) => {
    const newRow = getEmptyRow(tableType);
    actualizarActaActual({
      [tableType]: [...actaVisita[tableType], newRow]
    });
  };

  const removeRowFromTable = (tableType, index) => {
    actualizarActaActual({
      [tableType]: actaVisita[tableType].filter((_, i) => i !== index)
    });
  };

  const updateTableRow = (tableType, index, field, value) => {
    // Validación de piso: permitir números, guiones y sin espacios
    if (field === 'piso') {
      if (value.includes(' ')) {
        Swal.fire({
          icon: 'error',
          title: 'Formato inválido',
          text: 'El campo piso no puede contener espacios.',
          timer: 2000,
          showConfirmButton: false
        });
        return;
      }
      // Opcionalmente: validar formato ^\d+(-\d+)?$ aquí o solo al guardar
    }

    const updatedTable = [...actaVisita[tableType]];
    updatedTable[index] = { ...updatedTable[index], [field]: value };

    // Validaciones específicas
    if (tableType === 'unidades_residenciales_detalle' || tableType === 'producto_detalle') {
      const ocupRaw = updatedTable[index].ocupadas;
      const vacRaw = updatedTable[index].vacias;
      const hasOcup = ocupRaw !== '' && ocupRaw !== null && ocupRaw !== undefined;
      const hasVac = vacRaw !== '' && vacRaw !== null && vacRaw !== undefined;
      if (hasOcup || hasVac) {
        const ocupadas = parseInt(ocupRaw) || 0;
        const vacias = parseInt(vacRaw) || 0;
        updatedTable[index].ur = (ocupadas + vacias).toString();
      } else {
        updatedTable[index].ur = '';
      }
    }

    if (tableType === 'unidades_no_residenciales_detalle') {
      // Establecer valores automáticos según actividad seleccionada
      if (field === 'actividad_nombre') {
        if (value === 'VACÍO' || value === 'AMOBLADO SIN USO') {
          updatedTable[index].ocupadas = '0';
          updatedTable[index].vacias = '1';
        } else if (value && value !== '') {
          updatedTable[index].ocupadas = '1';
          updatedTable[index].vacias = '0';
        }
      }

      // Calcular UNR como suma de ocupadas y vacías, dejando vacío si no hay datos
      const ocupRaw2 = updatedTable[index].ocupadas;
      const vacRaw2 = updatedTable[index].vacias;
      const hasOcup2 = ocupRaw2 !== '' && ocupRaw2 !== null && ocupRaw2 !== undefined;
      const hasVac2 = vacRaw2 !== '' && vacRaw2 !== null && vacRaw2 !== undefined;
      if (hasOcup2 || hasVac2) {
        const ocupadas = parseInt(ocupRaw2) || 0;
        const vacias = parseInt(vacRaw2) || 0;
        updatedTable[index].unr = (ocupadas + vacias).toString();
      } else {
        updatedTable[index].unr = '';
      }

      // Calcular área cuando cambian las dimensiones, dejando vacío si falta alguna
      if (field === 'largo' || field === 'ancho') {
        const largoStr = updatedTable[index].largo;
        const anchoStr = updatedTable[index].ancho;
        const hasL = largoStr !== '' && largoStr !== null && largoStr !== undefined;
        const hasA = anchoStr !== '' && anchoStr !== null && anchoStr !== undefined;
        if (hasL && hasA) {
          const largo = parseFloat(largoStr);
          const ancho = parseFloat(anchoStr);
          if (!isNaN(largo) && !isNaN(ancho)) {
            updatedTable[index].area = (largo * ancho).toFixed(2);
          } else {
            updatedTable[index].area = '';
          }
        } else {
          updatedTable[index].area = '';
        }
      }
    }

    if (tableType === 'recipientes_detalle') {
      if (field === 'largo' || field === 'ancho' || field === 'alto') {
        const lStr = updatedTable[index].largo;
        const aStr = updatedTable[index].ancho;
        const hStr = updatedTable[index].alto;
        const allPresent = [lStr, aStr, hStr].every(v => v !== '' && v !== null && v !== undefined);
        if (allPresent) {
          const largo = parseFloat(lStr);
          const ancho = parseFloat(aStr);
          const alto = parseFloat(hStr);
          if (![largo, ancho, alto].some(n => isNaN(n))) {
            updatedTable[index].m3 = (largo * ancho * alto).toFixed(2);
          } else {
            updatedTable[index].m3 = '';
          }
        } else {
          updatedTable[index].m3 = '';
        }
      }
      // Al cambiar cualquier medida/cantidad, activar cálculo automático y desactivar override manual
      if (['largo', 'ancho', 'alto', 'cantidad', 'm3'].includes(field)) {
        setUsarCalculoValorM3(true);
      }
    }

    // Actualizar la tabla en el acta actual
    actualizarActaActual({ [tableType]: updatedTable });
  };

  const getEmptyRow = (tableType) => {
    switch (tableType) {
      case 'unidades_residenciales_detalle':
      case 'producto_detalle':
        return { piso: '', ur: '', ocupadas: '', vacias: '' };
      case 'unidades_no_residenciales_detalle':
        return {
          piso: '', unr: '', actividad_nombre: '', producto: '',
          largo: '', ancho: '', area: '', ocupadas: '', vacias: ''
        };
      case 'recipientes_detalle':
        return {
          descripcion: '', largo: '', ancho: '', alto: '', cantidad: '', m3: ''
        };
      default:
        return {};
    }
  };

  // Navegación entre pasos
  const nextStep = () => {
    if (currentStep < totalSteps) {
      let nextStepNumber = currentStep + 1;

      // Saltar pasos bloqueados
      while (nextStepNumber <= totalSteps && isStepBlocked(nextStepNumber)) {
        nextStepNumber++;
      }

      if (nextStepNumber <= totalSteps) {
        setCurrentStep(nextStepNumber);
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      let prevStepNumber = currentStep - 1;

      // Saltar pasos bloqueados hacia atrás
      while (prevStepNumber >= 1 && isStepBlocked(prevStepNumber)) {
        prevStepNumber--;
      }

      if (prevStepNumber >= 1) {
        setCurrentStep(prevStepNumber);
      }
    }
  };

  const goToStep = (step) => {
    // Verificar si el paso está bloqueado
    if (isStepBlocked(step)) {
      return;
    }
    setCurrentStep(step);
  };


  const [actividadesOptions, setActividadesOptions] = useState([]);

  useEffect(() => {
    const fetchActividades = async () => {
      try {
        const response = await getActividadesEconomicasFilter('?limit=1000');
        if (response && response.data && response.data.results) {
          const sorted = response.data.results.sort((a, b) => a.nombre.localeCompare(b.nombre));
          const options = sorted.map(act => ({
            value: act.nombre,
            label: act.nombre
          }));
          setActividadesOptions(options);
        }
      } catch (error) {
        console.error("Error fetching actividades economicas", error);
      }
    };
    fetchActividades();
  }, []);

  // Estilos personalizados para react-select
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '31px',
      height: '31px',
      fontSize: '14px',
      border: 'none',
      backgroundColor: 'transparent',
      boxShadow: 'none',
      '&:hover': {
        border: 'none'
      }
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: '31px',
      padding: '0 6px'
    }),
    input: (provided) => ({
      ...provided,
      margin: '0px',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: '31px',
    }),
    placeholder: (provided) => ({
      ...provided,
      fontSize: '14px',
      color: '#6c757d'
    }),
    singleValue: (provided) => ({
      ...provided,
      fontSize: '14px'
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      fontSize: '14px'
    }),
    option: (provided, state) => ({
      ...provided,
      fontSize: '14px',
      padding: '8px 12px'
    })
  };

  // Componente para renderizar tabla dinámica
  const renderDynamicTable = (tableType, title, icon, color, fields) => {
    let data = actaVisita[tableType] || [];

    // Manejar datos que vienen como string JSON del servidor
    if (typeof data === 'string' && data.trim() !== '') {
      try {
        const parsedData = JSON.parse(data);
        data = parsedData;
        // Actualizar el estado con los datos parseados si es diferente
        // Solo actualizar si realmente es diferente para evitar loops
        if (typeof actaVisita[tableType] === 'string') {
          setTimeout(() => {
            actualizarActaActual({ [tableType]: parsedData });
          }, 0);
        }
      } catch (e) {
        console.error(`Error parsing ${tableType} JSON:`, e);
        data = [];
      }
    }

    // Asegurar que data sea un array
    if (!Array.isArray(data)) {
      data = [];
    }

    // Debug para recipientes_detalle
    if (tableType === 'recipientes_detalle') {
      console.log('renderDynamicTable - recipientes_detalle data:', data);
      console.log('renderDynamicTable - actaVisita[recipientes_detalle]:', actaVisita.recipientes_detalle);
    }

    // Función para inicializar tabla con una fila por defecto
    const initializeTableWithDefaultRow = () => {
      if (data.length === 0) {
        const newRow = getEmptyRow(tableType);
        actualizarActaActual({ [tableType]: [newRow] });
        data = [newRow];
      }
    };

    // Fuerza fila inicial inmediata para recipientes_detalle para evitar pérdida de foco en primer carácter
    if (tableType === 'recipientes_detalle' && data.length === 0) {
      initializeTableWithDefaultRow();
    }



    return (
      <div className="mb-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="d-flex align-items-center">
            <div style={{
              backgroundColor: color,
              borderRadius: '8px',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '12px'
            }}>
              <i className={`fas fa-${icon} text-white`}></i>
            </div>
            <h6 className="fw-bold mb-0" style={{ color: color }}>{title}</h6>
          </div>

          {/* Botón para agregar nueva fila - Oculto para Detalles de Recipientes */}
          {!isCasoFinalizado && tableType !== 'recipientes_detalle' && (
            <Button
              color="success"
              size="sm"
              onClick={() => addRowToTable(tableType)}
              className="btn-sm"
            >
              <i className="fas fa-plus me-2"></i>
              Agregar {title === 'Unidades Residenciales (UR)' ? 'Unidad Residencial' :
                title === 'Unidades No Residenciales (UNR)' ? 'Unidad No Residencial' : 'Fila'}
            </Button>
          )}
        </div>

        <div className="table-responsive">
          <table className="table table-bordered table-sm table-hover">
            <thead className="table-success">
              <tr>
                {fields.map(field => (
                  <th key={field.key} style={{ minWidth: field.width || '100px' }}>
                    {field.label}
                  </th>
                ))}
                <th style={{ width: '60px', textAlign: 'center' }}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {data?.length > 0 ? (
                Array.isArray(data) ? data.map((row, index) => (
                  <tr key={index} className="align-middle">
                    {fields.map(field => (
                      <td key={field.key} className="p-2">
                        {field.key === 'actividad_nombre' && tableType === 'unidades_no_residenciales_detalle' ? (
                          <Select
                            options={actividadesOptions}
                            value={actividadesOptions.find(option => option.value === (row[field.key] || '')) || null}
                            onChange={(selectedOption) => updateTableRow(tableType, index, field.key, selectedOption ? selectedOption.value : '')}
                            isDisabled={field.disabled || isCasoFinalizado || (tableType === 'recipientes_detalle' && actaVisita.cancelado)}
                            placeholder="Seleccione una actividad"
                            isSearchable={true}
                            isClearable={true}
                            styles={customSelectStyles}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                          />
                        ) : (
                          <Input
                            type={field.type || 'text'}
                            step={field.step}
                            value={row[field.key] || ''}
                            onChange={(e) => updateTableRow(tableType, index, field.key, e.target.value)}
                            disabled={field.disabled || isCasoFinalizado || (tableType === 'recipientes_detalle' && actaVisita.cancelado) || ((field.key === 'ocupadas' || field.key === 'vacias') && tableType === 'unidades_no_residenciales_detalle' && (row.actividad_nombre === 'VACÍO' || row.actividad_nombre === 'AMOBLADO SIN USO'))}
                            style={(field.disabled || ((field.key === 'ocupadas' || field.key === 'vacias') && tableType === 'unidades_no_residenciales_detalle' && (row.actividad_nombre === 'VACÍO' || row.actividad_nombre === 'AMOBLADO SIN USO'))) ? { backgroundColor: '#f8f9fa' } : {}}
                            size="sm"
                            className="border-0 bg-transparent"
                          />
                        )}
                      </td>
                    ))}
                    <td className="text-center p-2">
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => removeRowFromTable(tableType, index)}
                        disabled={isCasoFinalizado || data.length === 1 || (tableType === 'recipientes_detalle' && actaVisita.cancelado)}
                        className="btn-sm"
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    </td>
                  </tr>
                )) : []
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Función para preparar datos del acta para envío
  const prepararDatosActa = (acta) => {
    const formData = new FormData();
    // Clonar acta para poder ajustar valores sin mutar el estado original
    const actaPayload = { ...acta };

    // Si está activo sin_firma, eliminar datos del representante del usuario
    if (actaPayload?.sin_firma) {
      actaPayload.representante_nombre = '';
      actaPayload.representante_cedula = '';
      actaPayload.representante_firma = null;
    }

    // Agregar todos los campos del acta (excluyendo campos de control y firmas)
    Object.keys(actaPayload).forEach(key => {
      if (!['esPrincipal', 'titulo', 'esNueva', 'servidorId', 'representante_firma', 'ciudad_limpia_firma'].includes(key)) {
        let value = actaPayload[key];

        // Convertir fecha_informe al formato del backend antes de guardar
        if (key === 'fecha_informe' && value) {
          value = convertirFechaParaGuardar(value);
        }

        // Manejar arrays como JSON
        if (Array.isArray(value)) {
          formData.append(key, JSON.stringify(value));
        } else {
          const stringValue = typeof value === 'boolean'
            ? value.toString()
            : value;
          formData.append(key, stringValue || '');
        }
      }
    });

    for (let [key, value] of formData.entries()) {
      console.log(`${key}: ${value}`);
    }

    // Manejar archivos de firma por separado - solo si son archivos válidos
    if (actaPayload.representante_firma instanceof File) {
      formData.append('representante_firma', actaPayload.representante_firma);
    }
    if (actaPayload.ciudad_limpia_firma instanceof File) {
      formData.append('ciudad_limpia_firma', actaPayload.ciudad_limpia_firma);
    }

    return formData;
  };

  // Estados para control de cambios y validaciones
  const [actaPrincipalTieneCambios, setActaPrincipalTieneCambios] = useState(false);
  const [actaAdicionalEnProceso, setActaAdicionalEnProceso] = useState(false);

  // Validación: cédula obligatoria para casos de tipo 'ESCOMBROS'
  const validarCedulaRepresentanteEscombros = (acta) => {
    const tipoCaso = caso?.tipo_caso;
    if (tipoCaso === 'ESCOMBROS' || tipoCaso === 'ESPECIALES') {
      // Si el acta está marcada como sin firma o cancelada, no aplicar esta validación
      if (acta?.sin_firma || acta?.cancelado) {
        return true;
      }
      const cedula = (acta?.representante_cedula || '').toString().trim();
      if (!cedula) {
        const mensaje = 'En casos de tipo "ESCOMBROS", la cédula del representante es obligatoria.';
        Swal.fire({
          icon: 'warning',
          title: 'Validación',
          text: mensaje,
          confirmButtonColor: '#dc3545'
        });
        return false;
      }
    }
    return true;
  };

  // Validación adicional Step 2 - UNR: prevenir guardado con filas incompletas
  const validarUNRCompleto = (acta) => {
    try {
      let unr = acta?.unidades_no_residenciales_detalle;
      if (!unr) return true;
      if (typeof unr === 'string') {
        try { unr = JSON.parse(unr || '[]'); } catch (_) { unr = []; }
      }
      if (!Array.isArray(unr) || unr.length === 0) return true;

      const requiredFields = ['piso', 'actividad_nombre', 'largo', 'ancho', 'ocupadas', 'vacias'];
      const isFilled = (v) => v !== '' && v !== null && v !== undefined;
      const pisoRegex = /^\d+(-\d+)?$/;

      for (let i = 0; i < unr.length; i++) {
        const row = unr[i] || {};
        // Detecta si hay datos parciales en la fila
        const anyValue = requiredFields.some((k) => isFilled(row[k]));
        if (anyValue) {
          const missing = requiredFields.filter((k) => !isFilled(row[k]));
          if (missing.length > 0) {
            Swal.fire({
              icon: 'error',
              title: 'No se puede guardar',
              text: `En Unidades No Residenciales, fila ${i + 1}: complete todos los campos obligatorios antes de guardar.`,
              confirmButtonColor: '#dc3545'
            });
            return false;
          }

          // Validar formato de piso
          if (isFilled(row.piso) && !pisoRegex.test(row.piso)) {
            Swal.fire({
              icon: 'error',
              title: 'Formato de Piso Inválido',
              text: `En Unidades No Residenciales, fila ${i + 1}: el piso debe ser un número entero o un rango (ej. 1-2) sin espacios.`,
              confirmButtonColor: '#dc3545'
            });
            return false;
          }
        }
      }
      return true;
    } catch (e) {
      // Si ocurre un error inesperado al validar, no bloquear guardado
      return true;
    }
  };

  // Validación adicional Step 2 - UR: prevenir guardado con filas incompletas
  const validarURCompleto = (acta) => {
    try {
      let ur = acta?.unidades_residenciales_detalle;
      if (!ur) return true;
      if (typeof ur === 'string') {
        try { ur = JSON.parse(ur || '[]'); } catch (_) { ur = []; }
      }
      if (!Array.isArray(ur) || ur.length === 0) return true;

      const requiredFields = ['piso', 'ur', 'ocupadas', 'vacias'];
      const isFilled = (v) => v !== '' && v !== null && v !== undefined;
      const pisoRegex = /^\d+(-\d+)?$/;

      for (let i = 0; i < ur.length; i++) {
        const row = ur[i] || {};
        // Detecta si hay datos parciales en la fila
        const anyValue = requiredFields.some((k) => isFilled(row[k]));
        if (anyValue) {
          const missing = requiredFields.filter((k) => !isFilled(row[k]));
          if (missing.length > 0) {
            Swal.fire({
              icon: 'error',
              title: 'No se puede guardar',
              text: `En Unidades Residenciales, fila ${i + 1}: complete todos los campos obligatorios (Piso, UR, Ocupadas, Vacías) antes de guardar.`,
              confirmButtonColor: '#dc3545'
            });
            return false;
          }

          // Validar formato de piso
          if (isFilled(row.piso) && !pisoRegex.test(row.piso)) {
            Swal.fire({
              icon: 'error',
              title: 'Formato de Piso Inválido',
              text: `En Unidades Residenciales, fila ${i + 1}: el piso debe ser un número entero o un rango (ej. 1-2) sin espacios.`,
              confirmButtonColor: '#dc3545'
            });
            return false;
          }
        }
      }
      return true;
    } catch (e) {
      // Si ocurre un error inesperado al validar, no bloquear guardado
      return true;
    }
  };

  // Validación condicional: campos obligatorios según banderas
  const validarCamposCondicionales = (acta) => {
    try {
      const errores = [];
      const isFilled = (v) => v !== '' && v !== null && v !== undefined;

      if (acta?.cuenta_suscriptor_activo && !isFilled(acta?.suscriptor_activo_detalles)) {
        errores.push('El campo "Detalles del Suscriptor Activo" es obligatorio cuando "Cuenta suscriptor activo" está marcado.');
      }
      if (acta?.demolido && !isFilled(acta?.causa_demolicion)) {
        errores.push('El campo "Causa de demolición" es obligatorio cuando "Demolido" está marcado.');
      }

      if (errores.length > 0) {
        Swal.fire({
          icon: 'error',
          title: 'Campos obligatorios faltantes',
          text: errores.join(' '),
          confirmButtonColor: '#dc3545'
        });
        return false;
      }
      return true;
    } catch (e) {
      // No bloquear guardado por errores inesperados de validación
      return true;
    }
  };

  // Nueva validación Paso 5 - Detalles de Recipientes: fila completa o vacía
  const validarRecipientesCompleto = (acta) => {
    try {
      let recipientes = acta?.recipientes_detalle;
      if (!recipientes) return true;
      if (typeof recipientes === 'string') {
        try { recipientes = JSON.parse(recipientes || '[]'); } catch (_) { recipientes = []; }
      }
      if (!Array.isArray(recipientes) || recipientes.length === 0) return true;

      const requiredFields = ['descripcion', 'largo', 'ancho', 'alto', 'cantidad'];
      const isFilled = (v) => v !== '' && v !== null && v !== undefined;

      for (let i = 0; i < recipientes.length; i++) {
        const row = recipientes[i] || {};
        const anyValue = requiredFields.some((k) => isFilled(row[k])) || isFilled(row.m3);
        if (anyValue) {
          const missingRequired = requiredFields.filter((k) => !isFilled(row[k]));
          const missingM3 = !isFilled(row.m3);
          if (missingRequired.length > 0 || missingM3) {
            Swal.fire({
              icon: 'error',
              title: 'No se puede guardar',
              text: `En Detalles de Recipientes, fila ${i + 1}: complete todos los campos (Recipiente, Largo, Ancho, Alto, Cantidad y m³) o deje la fila completamente vacía.`,
              confirmButtonColor: '#dc3545'
            });
            return false;
          }
        }
      }
      return true;
    } catch (e) {
      return true;
    }
  };

  // Nueva validación Paso 5 - Tipo de Recolección (Obligatorio solo para ESCOMBROS y ESPECIALES)
  const validarTipoRecoleccion = (acta) => {
    const tipoCaso = caso?.tipo_caso;
    // Solo validar si el paso 5 está habilitado (ESCOMBROS o ESPECIALES)
    if (tipoCaso !== 'ESCOMBROS' && tipoCaso !== 'ESPECIALES') return true;
    if (acta.cancelado) return true;

    if (typeof acta.tipo_recoleccion !== 'string' || acta.tipo_recoleccion.trim() === '') {
      Swal.fire({
        icon: 'error',
        title: 'No se puede guardar',
        text: 'El campo "Tipo" en Tipos de Residuos y Recipientes es obligatorio.',
        confirmButtonColor: '#dc3545'
      });
      return false;
    }
    return true;
  };

  // Guardar acta principal - solo usa patchUpdateActa
  const guardarActaPrincipal = async () => {
    if (!actaVisita.esPrincipal) {
      console.error('Esta función solo debe usarse para el acta principal');
      return;
    }

    // Validar cédula de representante cuando el caso es ESCOMBROS
    if (!validarCedulaRepresentanteEscombros(actaVisita)) {
      return;
    }
    // Validación UR: impedir guardado con filas incompletas en Step 2
    if (!validarURCompleto(actaVisita)) {
      return;
    }
    // Validación UNR: impedir guardado con filas incompletas en Step 2
    if (!validarUNRCompleto(actaVisita)) {
      return;
    }
    // Validación condicional: campos obligatorios según banderas
    if (!validarCamposCondicionales(actaVisita)) {
      return;
    }
    // Validación por tipo_orden (PQRS/RCD)
    if (!validarPorTipoOrden(actaVisita)) {
      return;
    }
    // Validación Recipientes: impedir guardado si hay filas incompletas o parcialmente llenas
    if (!validarRecipientesCompleto(actaVisita)) {
      return;
    }
    // Validación Tipo de Recolección: campo obligatorio
    if (!validarTipoRecoleccion(actaVisita)) {
      return;
    }

    // Validación de concordancia con información de referencia
    const refUR = parseInt(caso?.cliente?.[0]?.datos_basicos_facturacion?.[0]?.unidades_residenciales || 0);
    const refUNR = parseInt(caso?.cliente?.[0]?.datos_basicos_facturacion?.[0]?.no_residenciales || 0);

    if (totalURCalculado !== refUR || totalUNRCalculado !== refUNR) {
      const result = await Swal.fire({
        icon: 'warning',
        title: 'Discrepancia detectada',
        text: 'Los totales de UR o UNR no coinciden con la información de referencia. ¿Desea guardar de todas formas?',
        showCancelButton: true,
        confirmButtonColor: '#2fa4e7',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Sí, guardar',
        cancelButtonText: 'Revisar'
      });
      if (!result.isConfirmed) return;
    }

    try {
      Swal.fire({
        title: 'Guardando Acta Principal...',
        text: 'Por favor espere',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const formData = prepararDatosActa(actaVisita);
      const response = await patchUpdateActa(caso.id, formData);

      // Actualizar el estado local después del guardado exitoso
      const actasActualizadas = [...actas];
      actasActualizadas[actaActualIndex] = { ...actaVisita };
      setActas(actasActualizadas);

      // Marcar que el acta principal ya no tiene cambios pendientes
      setActaPrincipalTieneCambios(false);

      Swal.fire({
        icon: 'success',
        title: 'Acta Principal Guardada',
        text: 'El acta principal se ha guardado correctamente',
        confirmButtonColor: '#2fa4e7'
      });

      // Refrescar el caso para que la data esté actualizada en PDF y UI
      await refrescarCaso();

      return response;

    } catch (error) {
      console.error('Error al guardar acta principal:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo guardar el acta principal. Por favor intente nuevamente.',
        confirmButtonColor: '#2fa4e7'
      });
      throw error;
    }
  };

  // Guardar nueva acta adicional - solo usa postActaAdicional
  const guardarNuevaActaAdicional = async () => {
    if (actaVisita.esPrincipal || (!actaVisita.esNueva && actaVisita.servidorId)) {
      console.error('Esta función solo debe usarse para nuevas actas adicionales');
      return;
    }

    try {
      // Validar que no hay cambios pendientes en acta principal
      if (actaPrincipalTieneCambios) {
        Swal.fire({
          icon: 'warning',
          title: 'Cambios Pendientes',
          text: 'Debe guardar los cambios del Acta Principal antes de crear una nueva acta adicional',
          confirmButtonColor: '#dc3545'
        });
        return;
      }

      // Validar que hay una acta adicional en proceso para guardar
      if (!actaAdicionalEnProceso) {
        Swal.fire({
          icon: 'warning',
          title: 'No hay Acta en Proceso',
          text: 'No hay una acta adicional en proceso para guardar',
          confirmButtonColor: '#dc3545'
        });
        return;
      }

      // Validar cédula de representante cuando el caso es ESCOMBROS
      if (!validarCedulaRepresentanteEscombros(actaVisita)) {
        return;
      }
      // Validación UR: impedir guardado con filas incompletas en Step 2
      if (!validarURCompleto(actaVisita)) {
        return;
      }
      // Validación UNR: impedir guardado con filas incompletas en Step 2
      if (!validarUNRCompleto(actaVisita)) {
        return;
      }
      // Validación condicional: campos obligatorios según banderas
      if (!validarCamposCondicionales(actaVisita)) {
        return;
      }
      // Validación por tipo_orden (PQRS/RCD)
      if (!validarPorTipoOrden(actaVisita)) {
        return;
      }
      // Validación Recipientes: impedir guardado si hay filas incompletas o parcialmente llenas
      if (!validarRecipientesCompleto(actaVisita)) {
        return;
      }
      // Validación Tipo de Recolección: campo obligatorio
      if (!validarTipoRecoleccion(actaVisita)) {
        return;
      }

      // Validación de concordancia con información de referencia
      const refUR = parseInt(caso?.cliente?.[0]?.datos_basicos_facturacion?.[0]?.unidades_residenciales || 0);
      const refUNR = parseInt(caso?.cliente?.[0]?.datos_basicos_facturacion?.[0]?.no_residenciales || 0);

      if (totalURCalculado !== refUR || totalUNRCalculado !== refUNR) {
        const result = await Swal.fire({
          icon: 'warning',
          title: 'Discrepancia detectada',
          text: 'Los totales de UR o UNR no coinciden con la información de referencia. ¿Desea guardar de todas formas?',
          showCancelButton: true,
          confirmButtonColor: '#2fa4e7',
          cancelButtonColor: '#6c757d',
          confirmButtonText: 'Sí, guardar',
          cancelButtonText: 'Revisar'
        });
        if (!result.isConfirmed) return;
      }

      setActaAdicionalEnProceso(true);

      Swal.fire({
        title: 'Creando Acta Adicional...',
        text: 'Por favor espere',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const formData = prepararDatosActa(actaVisita);
      const response = await postActaAdicional(caso.id, formData);

      // Actualizar el acta con el ID del servidor
      const actaActualizada = {
        ...actaVisita,
        esNueva: false,
        servidorId: response?.data?.id || response?.id
      };

      setActaVisita(actaActualizada);

      // Actualizar el estado local
      const actasActualizadas = [...actas];
      actasActualizadas[actaActualIndex] = actaActualizada;
      setActas(actasActualizadas);

      setActaAdicionalEnProceso(false);

      Swal.fire({
        icon: 'success',
        title: 'Acta Adicional Creada',
        text: `${actaVisita.titulo} se ha creado correctamente`,
        confirmButtonColor: '#2fa4e7'
      });

      // Refrescar el caso para sincronizar informes adicionales
      await refrescarCaso();

      return response;

    } catch (error) {
      console.error('Error al crear acta adicional:', error);
      setActaAdicionalEnProceso(false);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo crear el acta adicional. Por favor intente nuevamente.',
        confirmButtonColor: '#2fa4e7'
      });
      throw error;
    }
  };

  // Actualizar acta adicional existente - solo usa patchUpdateActaAdicional
  const actualizarActaAdicional = async () => {
    if (actaVisita.esPrincipal || actaVisita.esNueva || !actaVisita.servidorId) {
      console.error('Esta función solo debe usarse para actas adicionales existentes');
      Swal.fire({
        icon: 'warning',
        title: 'Error de Validación',
        text: 'Esta acta no puede ser actualizada. Verifique que sea una acta adicional guardada.',
        confirmButtonColor: '#dc3545'
      });
      return;
    }

    try {
      // Validar cédula de representante cuando el caso es ESCOMBROS
      if (!validarCedulaRepresentanteEscombros(actaVisita)) {
        return;
      }
      // Validación UR: impedir guardado con filas incompletas en Step 2
      if (!validarURCompleto(actaVisita)) {
        return;
      }
      // Validación UNR: impedir guardado con filas incompletas en Step 2
      if (!validarUNRCompleto(actaVisita)) {
        return;
      }
      // Validación condicional: campos obligatorios según banderas
      if (!validarCamposCondicionales(actaVisita)) {
        return;
      }
      // Validación por tipo_orden (PQRS/RCD)
      if (!validarPorTipoOrden(actaVisita)) {
        return;
      }
      // Validación Recipientes: impedir guardado si hay filas incompletas o parcialmente llenas
      if (!validarRecipientesCompleto(actaVisita)) {
        return;
      }
      // Validación Tipo de Recolección: campo obligatorio
      if (!validarTipoRecoleccion(actaVisita)) {
        return;
      }

      // Validación de concordancia con información de referencia
      const refUR = parseInt(caso?.cliente?.[0]?.datos_basicos_facturacion?.[0]?.unidades_residenciales || 0);
      const refUNR = parseInt(caso?.cliente?.[0]?.datos_basicos_facturacion?.[0]?.no_residenciales || 0);

      if (totalURCalculado !== refUR || totalUNRCalculado !== refUNR) {
        const result = await Swal.fire({
          icon: 'warning',
          title: 'Discrepancia detectada',
          text: 'Los totales de UR o UNR no coinciden con la información de referencia. ¿Desea guardar de todas formas?',
          showCancelButton: true,
          confirmButtonColor: '#2fa4e7',
          cancelButtonColor: '#6c757d',
          confirmButtonText: 'Sí, guardar',
          cancelButtonText: 'Revisar'
        });
        if (!result.isConfirmed) return;
      }

      Swal.fire({
        title: 'Actualizando Acta Adicional...',
        text: 'Por favor espere',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const formData = prepararDatosActa(actaVisita);
      console.log('Actualizando acta con ID:', actaVisita.servidorId);
      const response = await patchUpdateActaAdicional(actaVisita.servidorId, formData);

      // Actualizar el estado local después del guardado exitoso
      const actasActualizadas = [...actas];
      actasActualizadas[actaActualIndex] = { ...actaVisita };
      setActas(actasActualizadas);

      Swal.fire({
        icon: 'success',
        title: 'Acta Adicional Actualizada',
        text: `${actaVisita.titulo} se ha actualizado correctamente`,
        confirmButtonColor: '#2fa4e7'
      });

      // Refrescar el caso para que la data esté actualizada
      await refrescarCaso();

      return response;

    } catch (error) {
      console.error('Error al actualizar acta adicional:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo actualizar el acta adicional. Por favor intente nuevamente.',
        confirmButtonColor: '#2fa4e7'
      });
      throw error;
    }
  };
  // Funciones para manejar las firmas
  const handleRepresentanteSignature = (signatureFile) => {
    // Actualizar el estado del acta
    setActaVisita(prev => ({
      ...prev,
      representante_firma: signatureFile
    }));
    // Guardar en estado temporal para preview inmediato
    setTempRepresentanteFirma(signatureFile);
    setShowRepresentanteSignature(false);
  };

  const handleCiudadLimpiaSignature = (signatureFile) => {
    // Actualizar el estado del acta
    setActaVisita(prev => ({
      ...prev,
      ciudad_limpia_firma: signatureFile
    }));
    // Guardar en estado temporal para preview inmediato
    setTempCiudadLimpiaFirma(signatureFile);
    setShowCiudadLimpiaSignature(false);
  };

  // Títulos de los pasos
  const stepTitles = [
    'Información de Visita',
    'Unidades y Productos',
    'Actualización de Datos',
    'Características del Predio',
    'Tipos de Residuos y Recipientes',
    'Observaciones',
    'Firmas y Representantes'
  ];

  // Función para convertir snake_case a título
  const formatLabel = (key) => {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  // Renderizar contenido del paso actual
  const renderStepContent = () => {
    // Verificar si el paso actual está bloqueado
    if (isStepBlocked(currentStep)) {
      return (
        <div className="text-center py-5">
          <div className="mb-3">
            <i className="fas fa-lock" style={{ fontSize: '3rem', color: '#dc3545' }}></i>
          </div>
          <h5 className="text-danger mb-3">Paso Bloqueado</h5>
          <p className="text-muted mb-4">
            Este paso no está disponible para el tipo de caso: <strong>{caso?.tipo_caso}</strong>
          </p>
          <Button color="primary" onClick={() => goToStep(findFirstAvailableStep())}>
            <i className="fas fa-arrow-left me-2"></i>
            Ir al primer paso disponible
          </Button>
        </div>
      );
    }

    switch (currentStep) {
      case 1:
        return (
          <div>
            <div className="d-flex align-items-center mb-4">
              <div style={{
                backgroundColor: '#556ee6',
                borderRadius: '8px',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '12px'
              }}>
                <i className="fas fa-calendar-alt text-white"></i>
              </div>
              <h5 className="fw-bold mb-0" style={{ color: '#556ee6' }}>Información de Visita</h5>
            </div>

            <Row className="g-4">
              {/* Columna izquierda - Formularios */}
              <Col lg={6}>
                <h6 className="mb-3" style={{ color: '#556ee6' }}>Información de la Visita</h6>
                <Row className="g-3">
                  <Col md={6}>
                    <FormGroup>
                      <Label>{formatLabel('fecha_visita')}</Label>
                      <Input
                        type="date"
                        name="fecha_visita"
                        value={actaVisita.fecha_visita}
                        onChange={handleActaChange}
                        size="sm"
                        disabled={isCasoFinalizado}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>{formatLabel('hora_visita')}</Label>
                      <Input
                        type="time"
                        name="hora_visita"
                        value={actaVisita.hora_visita}
                        onChange={handleActaChange}
                        size="sm"
                        disabled={isCasoFinalizado}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>{formatLabel('ciudad_visita')}</Label>
                      <Input
                        type="text"
                        name="ciudad_visita"
                        value={actaVisita.ciudad_visita}
                        onChange={handleActaChange}
                        size="sm"
                        placeholder="Ej: Bogotá"
                        disabled={isCasoFinalizado}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Cuenta Enel / Energía</Label>
                      <div className="d-flex gap-2">
                        <Input
                          type="text"
                          name="cuenta_energia"
                          value={actaVisita.cuenta_energia}
                          onChange={handleActaChange}
                          size="sm"
                          placeholder="Número de cuenta energía"
                          disabled={isCasoFinalizado}
                          style={{
                            flex: 1,
                            ...(!actaVisita.cuenta_energia ? { borderColor: '#f1b44c', backgroundColor: '#fff8e6' } : {})
                          }}
                        />
                        <Button
                          color="warning"
                          size="sm"
                          onClick={() => setShowHomologarModal(true)}
                          disabled={isCasoFinalizado}
                          style={{ minWidth: '130px', whiteSpace: 'nowrap' }}
                          title="Homologar la cuenta Enel de este caso"
                        >
                          <i className="mdi mdi-swap-horizontal me-1"></i>
                          Homologar
                        </Button>
                      </div>
                      {!actaVisita.cuenta_energia && !isCasoFinalizado && (
                        <div className="text-warning small mt-1 fw-bold">
                          <i className="fas fa-exclamation-triangle me-1"></i>
                          Este campo está vacío
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
                <h6 className="mt-4 mb-3" style={{ color: '#556ee6' }}>Información del Representante</h6>
                <Row className="g-3">
                  <Col md={6}>
                    <FormGroup>
                      <Label>{formatLabel('representante_nombre')}</Label>
                      <Input
                        type="text"
                        name="representante_nombre"
                        value={actaVisita.sin_firma ? '' : actaVisita.representante_nombre}
                        onChange={handleActaChange}
                        size="sm"
                        placeholder="Nombre del representante"
                        disabled={isCasoFinalizado || !actaVisita.esPrincipal || actaVisita.sin_firma}
                      />
                      {!actaVisita.esPrincipal && (
                        <small className="text-muted">
                          <i className="fas fa-info-circle me-1"></i>
                          Este campo se hereda del acta principal
                        </small>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>{formatLabel('representante_cedula')}</Label>
                      <Input
                        type="text"
                        name="representante_cedula"
                        value={actaVisita.sin_firma ? '' : actaVisita.representante_cedula}
                        onChange={handleActaChange}
                        size="sm"
                        placeholder="Cédula del representante"
                        disabled={isCasoFinalizado || !actaVisita.esPrincipal || actaVisita.sin_firma}
                      />
                      {!actaVisita.esPrincipal && (
                        <small className="text-muted">
                          <i className="fas fa-info-circle me-1"></i>
                          Este campo se hereda del acta principal
                        </small>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={12}>
                    <FormGroup>
                      <Label>{formatLabel('representante_telefono_celular')}</Label>
                      <Input
                        type="text"
                        name="representante_telefono_celular"
                        value={actaVisita.representante_telefono_celular}
                        onChange={handleActaChange}
                        size="sm"
                        placeholder="Teléfono del representante"
                        disabled={isCasoFinalizado || !actaVisita.esPrincipal}
                      />
                      {!actaVisita.esPrincipal && (
                        <small className="text-muted">
                          <i className="fas fa-info-circle me-1"></i>
                          Este campo se hereda del acta principal
                        </small>
                      )}
                    </FormGroup>
                  </Col>
                  {/* Check: Sin firma (bloquea edición de datos del representante) */}
                  <Col md={12}>
                    <FormGroup check>
                      <Input
                        type="checkbox"
                        id="sin_firma"
                        name="sin_firma"
                        checked={!!actaVisita.sin_firma}
                        onChange={handleActaChange}
                        disabled={isCasoFinalizado || !actaVisita.esPrincipal}
                      />
                      <Label check for="sin_firma" className="ms-2">
                        No recolectar ni modificar datos del representante (sin firma)
                      </Label>
                    </FormGroup>
                    {actaVisita.sin_firma && (
                      <small className="text-danger d-block mt-1">
                        Al activar esta opción se bloquean nombre, cédula y la captura de firma del representante.
                      </small>
                    )}
                  </Col>
                </Row>

                {/* Información de Facturación - Solo para actas adicionales */}
                {!actaVisita.esPrincipal && (
                  <>
                    <h6 className="mt-4 mb-3" style={{ color: '#556ee6' }}>Información de Facturación</h6>
                    <Row className="g-3">
                      <Col md={6}>
                        <FormGroup>
                          <Label>{formatLabel('cuenta_suscriptor')}</Label>
                          <div className="d-flex gap-2">
                            <Input
                              type="text"
                              name="cuenta_suscriptor"
                              value={actaVisita.cuenta_suscriptor}
                              onChange={handleActaChange}
                              onKeyPress={handleCuentaSuscriptorKeyPress}
                              size="sm"
                              placeholder="Número de cuenta suscriptor"
                              disabled={isCasoFinalizado || searchingContact}
                              style={{ flex: 1 }}
                            />
                            <Button
                              color="primary"
                              size="sm"
                              onClick={handleBuscarContacto}
                              disabled={isCasoFinalizado || searchingContact || !actaVisita.cuenta_suscriptor?.trim()}
                              style={{ minWidth: '100px' }}
                            >
                              {searchingContact ? (
                                <>
                                  <i className="fas fa-spinner fa-spin me-1"></i>
                                  Buscando...
                                </>
                              ) : (
                                <>
                                  <i className="fas fa-search me-1"></i>
                                  Buscar
                                </>
                              )}
                            </Button>
                          </div>
                          <small className="text-muted mt-1 d-block">
                            <i className="fas fa-info-circle me-1"></i>
                            Presiona Enter o haz clic en "Buscar" para buscar el contacto
                          </small>
                          {contactSearchError && (
                            <small className="text-danger d-block mt-1">
                              <i className="fas fa-exclamation-triangle me-1"></i>
                              {contactSearchError}
                            </small>
                          )}
                        </FormGroup>
                      </Col>

                      <Col md={6}>
                        <FormGroup>
                          <Label>{formatLabel('nombre_cliente')}</Label>
                          <Input
                            type="text"
                            name="nombre_cliente"
                            value={actaVisita.nombre_cliente}
                            onChange={handleActaChange}
                            size="sm"
                            placeholder="Nombre del cliente"
                            disabled={isCasoFinalizado}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>{formatLabel('direccion_informe')}</Label>
                          <Input
                            type="text"
                            name="direccion_informe"
                            value={actaVisita.direccion_informe}
                            onChange={handleActaChange}
                            size="sm"
                            placeholder="Dirección de la visita"
                            disabled={isCasoFinalizado}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={12}>
                        <FormGroup>
                          <Label>{formatLabel('direccion')}</Label>
                          <Input
                            type="text"
                            name="direccion"
                            value={actaVisita.direccion}
                            onChange={handleActaChange}
                            size="sm"
                            placeholder="Dirección del cliente"
                            disabled={isCasoFinalizado}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </>
                )}
              </Col>

              {/* Columna derecha - Vista previa */}
              <Col lg={6}>
                <div className="card border-primary shadow-sm">
                  <div className="card-body" style={{ backgroundColor: '#f8f9fc', padding: '1.5rem' }}>
                    <div style={{
                      backgroundColor: 'white',
                      padding: '1.5rem',
                      borderRadius: '8px',
                      border: '1px solid #e3e6f0',
                      fontFamily: 'Georgia, serif',
                      lineHeight: '1.6',
                      fontSize: '14px'
                    }}>
                      <p className="mb-3" style={{ textAlign: 'justify' }}>
                        En la Ciudad de <strong style={{ color: '#556ee6' }}>{actaVisita.ciudad_visita || '_______'}</strong> siendo las <strong style={{ color: '#556ee6' }}>{actaVisita.hora_visita || '_______'}</strong> a los <strong style={{ color: '#556ee6' }}>{actaVisita.dia_visita || '___'}</strong> días del mes de <strong style={{ color: '#556ee6' }}>{actaVisita.mes_visita || '_______'}</strong> del año <strong style={{ color: '#556ee6' }}>{actaVisita.año_visita || '____'}</strong>
                      </p>
                      <p className="mb-0" style={{ textAlign: 'justify' }}>
                        se reunieron el señor <strong style={{ color: '#28a745' }}>{(actaVisita.sin_firma || !actaVisita.representante_nombre) ? '________________________' : actaVisita.representante_nombre}</strong> en su calidad de usuario, y el señor <strong style={{ color: '#dc3545' }}>{actaVisita.ciudad_limpia_nombre || '________________________'}</strong> como representante de Ciudad Limpia en su calidad de empresa prestadora del servicio de recolección, para dar constancia de los resultados obtenidos en la presente visita técnica.
                      </p>
                    </div>

                    {/* Información adicional */}
                    <div className="mt-3">
                      <small className="text-muted d-block mb-1">
                        <i className="mdi mdi-information-outline me-1"></i>
                        Esta vista previa se actualiza automáticamente
                      </small>
                      {(actaVisita.archivo_no || actaVisita.no_pqrs) && (
                        <div className="d-flex gap-3 mt-2">
                          {actaVisita.archivo_no && (
                            <small className="badge bg-light text-dark">
                              Archivo: {actaVisita.archivo_no}
                            </small>
                          )}
                          {actaVisita.no_pqrs && (
                            <small className="badge bg-light text-dark">
                              PQRS: {actaVisita.no_pqrs}
                            </small>
                          )}
                          {actaVisita.cuenta_energia && (
                            <small className="badge bg-light text-dark">
                              Cuenta Enel: {actaVisita.cuenta_energia}
                            </small>
                          )}
                          {actaVisita.cuenta_suscriptor && (
                            <small className="badge bg-light text-dark">
                              Cuenta Suscriptor: {actaVisita.cuenta_suscriptor}
                            </small>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        );

      case 2:
        return (
          <div>
            {/* Cuadro informativo con datos de facturación del cliente */}
            {caso?.cliente?.[0]?.datos_basicos_facturacion?.[0] && (
              <Card className="border-0 shadow-sm mb-4" style={{ backgroundColor: '#f8f9fc' }}>
                <CardBody className="p-3">
                  <div className="d-flex align-items-center mb-3">
                    <div style={{
                      borderRadius: '8px',
                      width: '36px',
                      height: '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '10px'
                    }}>
                      <i className="fas fa-info-circle text-white" style={{ fontSize: '16px' }}></i>
                    </div>
                    <div>
                      <h6 className="mb-1" style={{ color: '#556ee6', fontWeight: '600' }}>Información de Referencia</h6>
                      <p className="text-muted mb-0" style={{ fontSize: '12px' }}>Datos básicos de facturación del cliente</p>
                    </div>
                  </div>

                  <div className="d-flex flex-wrap justify-content-between" style={{ gap: '8px' }}>
                    <div className="info-card flex-fill" style={{
                      borderRadius: '8px',
                      border: '1px solid #e3e6f0',
                      textAlign: 'center',
                      padding: '12px 8px',
                      minWidth: '90px',
                      maxWidth: '140px'
                    }}>
                      <div className="mb-1">
                        <i className="fas fa-home" style={{ color: '#34c38f', fontSize: '20px' }}></i>
                      </div>
                      <div className="small" style={{ color: '#34c38f', fontWeight: '600', fontSize: '10px', marginBottom: '4px' }}>UNID. RESID.</div>
                      <div style={{ color: '#495057', fontWeight: 'bold', fontSize: '18px' }}>
                        {caso.cliente[0].datos_basicos_facturacion[0].unidades_residenciales || '0'}
                      </div>
                    </div>

                    <div className="info-card flex-fill" style={{
                      borderRadius: '8px',
                      border: '1px solid #e3e6f0',
                      textAlign: 'center',
                      padding: '12px 8px',
                      minWidth: '90px',
                      maxWidth: '140px'
                    }}>
                      <div className="mb-1">
                        <i className="fas fa-building" style={{ color: '#f1b44c', fontSize: '20px' }}></i>
                      </div>
                      <div className="small" style={{ color: '#f1b44c', fontWeight: '600', fontSize: '10px', marginBottom: '4px' }}>UNID. NO RESID.</div>
                      <div style={{ color: '#495057', fontWeight: 'bold', fontSize: '18px' }}>
                        {caso.cliente[0].datos_basicos_facturacion[0].no_residenciales || '0'}
                      </div>
                    </div>

                    <div className="info-card flex-fill" style={{
                      borderRadius: '8px',
                      border: '1px solid #e3e6f0',
                      textAlign: 'center',
                      padding: '12px 8px',
                      minWidth: '90px',
                      maxWidth: '140px'
                    }}>
                      <div className="mb-1">
                        <i className="fas fa-home-alt" style={{ color: '#dc3545', fontSize: '20px' }}></i>
                      </div>
                      <div className="small" style={{ color: '#dc3545', fontWeight: '600', fontSize: '10px', marginBottom: '4px' }}>RESID. VACÍAS</div>
                      <div style={{ color: '#495057', fontWeight: 'bold', fontSize: '18px' }}>
                        {caso.cliente[0].datos_basicos_facturacion[0].residenciales_vacias || '0'}
                      </div>
                    </div>

                    <div className="info-card flex-fill" style={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      border: '1px solid #e3e6f0',
                      textAlign: 'center',
                      padding: '12px 8px',
                      minWidth: '90px',
                      maxWidth: '140px'
                    }}>
                      <div className="mb-1">
                        <i className="fas fa-building-slash" style={{ color: '#6f42c1', fontSize: '20px' }}></i>
                      </div>
                      <div className="small" style={{ color: '#6f42c1', fontWeight: '600', fontSize: '10px', marginBottom: '4px' }}>NO RESID. VACÍAS</div>
                      <div style={{ color: '#495057', fontWeight: 'bold', fontSize: '18px' }}>
                        {caso.cliente[0].datos_basicos_facturacion[0].no_residenciales_vacias || '0'}
                      </div>
                    </div>

                    <div className="info-card flex-fill" style={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      border: '1px solid #e3e6f0',
                      textAlign: 'center',
                      padding: '12px 8px',
                      minWidth: '90px',
                      maxWidth: '140px'
                    }}>
                      <div className="mb-1">
                        <i className="fas fa-cube" style={{ color: '#17a2b8', fontSize: '20px' }}></i>
                      </div>
                      <div className="small" style={{ color: '#17a2b8', fontWeight: '600', fontSize: '10px', marginBottom: '4px' }}>VOLUMEN M³</div>
                      <div style={{ color: '#495057', fontWeight: 'bold', fontSize: '18px' }}>
                        {caso.cliente[0].datos_basicos_facturacion[0].volumen_m3 || '0'}
                      </div>
                    </div>

                    <div className="info-card flex-fill" style={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      border: '1px solid #e3e6f0',
                      textAlign: 'center',
                      padding: '12px 8px',
                      minWidth: '90px',
                      maxWidth: '140px'
                    }}>
                      <div className="mb-1">
                        <i className="fas fa-tag" style={{ color: '#fd7e14', fontSize: '20px' }}></i>
                      </div>
                      <div className="small" style={{ color: '#fd7e14', fontWeight: '600', fontSize: '10px', marginBottom: '4px' }}>CLASE USO</div>
                      <div style={{ color: '#495057', fontWeight: 'bold', fontSize: '14px' }}>
                        {caso.cliente[0].datos_basicos_facturacion[0].clase_uso || 'N/A'}
                      </div>
                    </div>

                    <div className="info-card flex-fill" style={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      border: '1px solid #e3e6f0',
                      textAlign: 'center',
                      padding: '12px 8px',
                      minWidth: '90px',
                      maxWidth: '140px'
                    }}>
                      <div className="mb-1">
                        <i className="fas fa-dollar-sign" style={{ color: '#28a745', fontSize: '20px' }}></i>
                      </div>
                      <div className="small" style={{ color: '#28a745', fontWeight: '600', fontSize: '10px', marginBottom: '4px' }}>CARTERA</div>
                      <div style={{ color: '#495057', fontWeight: 'bold', fontSize: '14px' }}>
                        ${parseInt(caso.cliente[0].datos_basicos_facturacion[0].cartera || '0').toLocaleString('es-CO')}
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 text-center">
                    <small className="text-muted" style={{ fontSize: '11px' }}>
                      <i className="mdi mdi-information-outline me-1"></i>
                      Información de referencia de los datos básicos de facturación del cliente
                    </small>
                  </div>
                </CardBody>
              </Card>
            )}

            {/* Comparación con Información de Referencia */}
            {(totalURCalculado !== parseInt(caso?.cliente?.[0]?.datos_basicos_facturacion?.[0]?.unidades_residenciales || 0) ||
              totalUNRCalculado !== parseInt(caso?.cliente?.[0]?.datos_basicos_facturacion?.[0]?.no_residenciales || 0)) && (
                <div className="alert alert-warning border-0 shadow-sm mb-4 d-flex align-items-center" style={{
                  backgroundColor: 'rgba(241, 180, 76, 0.15)',
                  borderLeft: '4px solid #f1b44c',
                  borderRadius: '8px'
                }}>
                  <i className="fas fa-exclamation-triangle text-warning me-3" style={{ fontSize: '24px' }}></i>
                  <div>
                    <h6 className="mb-1 fw-bold text-dark">Discrepancia con Información de Referencia</h6>
                    <p className="mb-0 small text-dark opacity-75">
                      Los totales ingresados no coinciden con los datos básicos de facturación.
                    </p>
                    <div className="d-flex gap-3 mt-2">
                      {totalURCalculado !== parseInt(caso?.cliente?.[0]?.datos_basicos_facturacion?.[0]?.unidades_residenciales || 0) && (
                        <span className="badge bg-light text-dark border">
                          UR: <span className="text-danger fw-bold">{totalURCalculado}</span> vs Ref: {caso?.cliente?.[0]?.datos_basicos_facturacion?.[0]?.unidades_residenciales || 0}
                        </span>
                      )}
                      {totalUNRCalculado !== parseInt(caso?.cliente?.[0]?.datos_basicos_facturacion?.[0]?.no_residenciales || 0) && (
                        <span className="badge bg-light text-dark border">
                          UNR: <span className="text-danger fw-bold">{totalUNRCalculado}</span> vs Ref: {caso?.cliente?.[0]?.datos_basicos_facturacion?.[0]?.no_residenciales || 0}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

            {/* Pestañas para las secciones */}
            <Card className="border-0 shadow-sm">
              <CardBody className="p-0">
                <Nav tabs className="nav-tabs-custom">
                  <NavItem>
                    <NavLink
                      className={`cursor-pointer ${activeTab === '1' ? 'active' : ''}`}
                      onClick={() => setActiveTab('1')}
                    >
                      <i className="fas fa-home me-2" style={{ color: '#34c38f' }}></i>
                      Unidades Residenciales
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={`cursor-pointer ${activeTab === '3' ? 'active' : ''}`}
                      onClick={() => setActiveTab('3')}
                    >
                      <i className="fas fa-building me-2" style={{ color: '#f1b44c' }}></i>
                      Unidades No Residenciales
                    </NavLink>
                  </NavItem>
                </Nav>

                <TabContent activeTab={activeTab} className="p-4">
                  <TabPane tabId="1">
                    <Row className="g-3 mb-4">
                      <Col lg={4}>
                        <FormGroup>
                          <Label className="fw-semibold">Nombre del Producto</Label>
                          <Input
                            type="number"
                            name="producto_nombre"
                            value={actaVisita.producto_nombre}
                            onChange={handleActaChange}
                            disabled={isCasoFinalizado}
                            placeholder="Ingrese el código del producto"
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    {renderDynamicTable(
                      'unidades_residenciales_detalle',
                      'Unidades Residenciales (UR)',
                      'home',
                      '#34c38f',
                      [
                        { key: 'piso', label: 'Piso', type: 'text', width: '100px' },
                        { key: 'ur', label: 'UR', type: 'number', disabled: true, width: '80px' },
                        { key: 'ocupadas', label: 'Ocupadas', type: 'number', width: '100px' },
                        { key: 'vacias', label: 'Vacías', type: 'number', width: '100px' }
                      ]
                    )}
                  </TabPane>

                  <TabPane tabId="3">
                    <Row className="g-3 mb-4">
                      <Col lg={4}>
                        <FormGroup>
                          <Label className="fw-semibold">Producto UNR</Label>
                          <Input
                            type="number"
                            name="producto_unr"
                            value={actaVisita.producto_unr}
                            onChange={handleActaChange}
                            disabled={isCasoFinalizado}
                            placeholder="Ingrese el código del producto UNR"
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    {renderDynamicTable(
                      'unidades_no_residenciales_detalle',
                      'Unidades No Residenciales (UNR)',
                      'building',
                      '#f1b44c',
                      [
                        { key: 'piso', label: 'Piso', type: 'text', width: '80px' },
                        { key: 'unr', label: 'UNR', type: 'number', disabled: true, width: '80px' },
                        { key: 'actividad_nombre', label: 'Actividad/Nombre', type: 'text', width: '350px' },
                        { key: 'largo', label: 'Largo (m)', type: 'number', step: '0.01', width: '100px' },
                        { key: 'ancho', label: 'Ancho (m)', type: 'number', step: '0.01', width: '100px' },
                        { key: 'area', label: 'Área (m²)', type: 'number', step: '0.01', disabled: true, width: '100px' },
                        { key: 'ocupadas', label: 'Ocupadas', type: 'number', width: '100px' },
                        { key: 'vacias', label: 'Vacías', type: 'number', width: '100px' }
                      ]
                    )}
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </div>
        );

      case 3:
        return (
          <div>
            <div className="d-flex align-items-center mb-4">
              <div style={{
                backgroundColor: '#50a5f1',
                borderRadius: '8px',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '12px'
              }}>
                <i className="fas fa-edit text-white"></i>
              </div>
              <h5 className="fw-bold mb-0" style={{ color: '#50a5f1' }}>Actualización de Datos</h5>
            </div>

            <Row className="g-3">
              <Col md={6}>
                <FormGroup>
                  <Label>{formatLabel('direccion_predio')}</Label>
                  <Input
                    type="text"
                    name="direccion_predio"
                    value={actaVisita.direccion_predio}
                    onChange={handleActaChange}
                    disabled={isCasoFinalizado}
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>{formatLabel('estrato_predio')}</Label>
                  <Input
                    type="select"
                    name="estrato_predio"
                    value={actaVisita.estrato_predio}
                    onChange={handleActaChange}
                    disabled={isCasoFinalizado}
                  >
                    <option value="">Seleccionar</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5</option>
                    <option value="6">6</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>{formatLabel('localidad_predio')}</Label>
                  <Input
                    type="select"
                    name="localidad_predio"
                    value={actaVisita.localidad_predio}
                    onChange={handleActaChange}
                    disabled={isCasoFinalizado}
                  >
                    <option value="">Seleccionar</option>
                    <option value="USAQUÉN">USAQUÉN</option>
                    <option value="CHAPINERO">CHAPINERO</option>
                    <option value="SANTA FE">SANTA FE</option>
                    <option value="SAN CRISTÓBAL">SAN CRISTÓBAL</option>
                    <option value="USME">USME</option>
                    <option value="TUNJUELITO">TUNJUELITO</option>
                    <option value="BOSA">BOSA</option>
                    <option value="KENNEDY">KENNEDY</option>
                    <option value="FONTIBÓN">FONTIBÓN</option>
                    <option value="ENGATIVÁ">ENGATIVÁ</option>
                    <option value="SUBA">SUBA</option>
                    <option value="BARRIOS UNIDOS">BARRIOS UNIDOS</option>
                    <option value="TEUSAQUILLO">TEUSAQUILLO</option>
                    <option value="LOS MÁRTIRES">LOS MÁRTIRES</option>
                    <option value="ANTONIO NARIÑO">ANTONIO NARIÑO</option>
                    <option value="PUENTE ARANDA">PUENTE ARANDA</option>
                    <option value="LA CANDELARIA">LA CANDELARIA</option>
                    <option value="RAFAEL URIBE URIBE">RAFAEL URIBE URIBE</option>
                    <option value="CIUDAD BOLÍVAR">CIUDAD BOLÍVAR</option>
                    <option value="SUMAPAZ">SUMAPAZ</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>{formatLabel('actualizacion_nombre_suscriptor')}</Label>
                  <Input
                    type="text"
                    name="actualizacion_nombre_suscriptor"
                    value={actaVisita.actualizacion_nombre_suscriptor}
                    onChange={handleActaChange}
                    disabled={isCasoFinalizado}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>{formatLabel('actualizacion_numero_meses_financiar')}</Label>
                  <Input
                    type="number"
                    name="actualizacion_numero_meses_financiar"
                    value={actaVisita.actualizacion_numero_meses_financiar}
                    onChange={handleActaChange}
                    disabled={isCasoFinalizado}
                  />
                </FormGroup>
              </Col>
            </Row>

          </div>
        );

      case 4:
        return (
          <div>
            <div className="d-flex align-items-center mb-4">
              <div style={{
                backgroundColor: '#f46a6a',
                borderRadius: '8px',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '12px'
              }}>
                <i className="fas fa-check-square text-white"></i>
              </div>
              <h5 className="fw-bold mb-0" style={{ color: '#f46a6a' }}>Características del Predio</h5>
            </div>

            <Row className="g-3">
              {[
                'abandonado', 'suscriptor_inactivo', 'demolido', 'en_construccion',
                'en_remodelacion', 'inexistente', 'inhabitable_o_en_ruinas', 'lote',
                'minimas_condiciones', 'programa_del_icbf', 'descuento_puerta_a_puerta',
                'se_cierra_pqr_como_no_atendido', 'areas_comunes', 'predio_que_se_observa_vacio',
                'predio_se_observa_ocupado', 'cuenta_suscriptor_activo'
              ].map((field, index) => (
                <Col md={6} key={field}>
                  <FormGroup check>
                    <Input
                      type="checkbox"
                      id={field}
                      name={field}
                      checked={actaVisita[field]}
                      onChange={handleActaChange}
                      disabled={isCasoFinalizado || actaVisita.cancelado}
                    />
                    <Label check for={field}>{formatLabel(field)}</Label>
                  </FormGroup>
                </Col>
              ))}
            </Row>

            {actaVisita.cuenta_suscriptor_activo && (
              <Row className="mt-3">
                <Col md={12}>
                  <FormGroup>
                    <Label>Detalles del Suscriptor Activo</Label>
                    <Input
                      type="number"
                      name="suscriptor_activo_detalles"
                      value={actaVisita.suscriptor_activo_detalles}
                      onChange={handleActaChange}
                      disabled={isCasoFinalizado}
                      placeholder="Ingrese datos numéricos"
                    />
                  </FormGroup>
                </Col>
              </Row>
            )}

            {actaVisita.demolido && (
              <Row className="mt-3">
                <Col md={12}>
                  <FormGroup>
                    <Label>{formatLabel('causa_demolicion')}</Label>
                    <Input
                      type="textarea"
                      name="causa_demolicion"
                      value={actaVisita.causa_demolicion}
                      onChange={handleActaChange}
                      disabled={isCasoFinalizado}
                      rows={2}
                    />
                  </FormGroup>
                </Col>
              </Row>
            )}
          </div>
        );

      case 5:
        return (
          <div>
            <div className="d-flex align-items-center mb-4">
              <div style={{
                backgroundColor: '#74788d',
                borderRadius: '8px',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '12px'
              }}>
                <i className="fas fa-trash text-white"></i>
              </div>
              <h5 className="fw-bold mb-0" style={{ color: '#74788d' }}>Tipos de Residuos y Recipientes</h5>
            </div>

            {tarifaActual && (
              <div className="alert alert-info mb-4" style={{ backgroundColor: '#e7f3ff', border: '1px solid #b8daff' }}>
                <div className="d-flex align-items-center">
                  <i className="fas fa-info-circle text-info me-2"></i>
                  <div>
                    <strong>Tarifa del Cliente:</strong> {tarifaActual.descripcion}<br />
                    <small className="text-muted">
                      Tarifa Normal: ${tarifaActual.valor_tarifa?.toLocaleString('es-CO')} |
                      Tarifa Especial: ${tarifaActual.tarifa_especial?.toLocaleString('es-CO')}
                      {tarifaActual.tarifa_lodo ? ` | Lodo Acueducto: $${parseFloat(tarifaActual.tarifa_lodo).toLocaleString('es-CO')}` : ''}
                    </small>
                  </div>
                </div>
              </div>
            )}

            <h6 className="mb-3" style={{ color: '#74788d' }}>Tipos de Residuos</h6>
            <Row className="g-3 mb-4">
              {[
                'escombros', 'especiales', 'para_relleno', 'para_escombrera',
                'enlonado', 'a_granel'
              ].map((field) => (
                <Col md={4} key={field}>
                  <FormGroup check>
                    <Input
                      type="checkbox"
                      id={field}
                      name={field}
                      checked={actaVisita[field]}
                      onChange={handleActaChange}
                      disabled={isCasoFinalizado}
                    />
                    <Label check for={field}>{formatLabel(field)}</Label>
                  </FormGroup>
                </Col>
              ))}
            </Row>

            <Row className="g-3 mb-4">
              <Col md={6}>
                <FormGroup>
                  <Label>{formatLabel('tipo')}</Label>
                  <Input
                    type="text"
                    name="tipo_recoleccion"
                    value={actaVisita.tipo_recoleccion}
                    onChange={handleActaChange}
                    disabled={isCasoFinalizado || actaVisita.cancelado}
                    placeholder="Ingrese el tipo de recolección"
                  />
                </FormGroup>
              </Col>
              <Col md={3} className="d-flex align-items-center">
                <FormGroup check className="mb-0">
                  <Input
                    type="checkbox"
                    id="tarifa_lodo"
                    name="tarifa_lodo"
                    checked={!!actaVisita.tarifa_lodo}
                    onChange={(e) => {
                      handleActaChange(e);
                      setUsarCalculoValorM3(true);
                    }}
                    disabled={isCasoFinalizado || actaVisita.cancelado}
                  />
                  <Label check for="tarifa_lodo" className="ms-1">
                    Lodo Acueducto
                    {tarifaActual?.tarifa_lodo && (
                      <small className="text-muted ms-1">(${parseFloat(tarifaActual.tarifa_lodo).toLocaleString('es-CO')}/m³)</small>
                    )}
                  </Label>
                </FormGroup>
              </Col>
            </Row>

            <h6 className="mb-3" style={{ color: '#74788d' }}>Recipientes</h6>
            <Card className="border-0 shadow-sm mb-4">
              <CardBody className="p-3">
                {renderDynamicTable(
                  'recipientes_detalle',
                  'Detalles de Recipientes',
                  'trash',
                  '#74788d',
                  [
                    { key: 'descripcion', label: 'Recipiente  ', type: 'text', width: '200px' },
                    { key: 'largo', label: 'Largo (m)', type: 'number', step: '0.01', width: '100px' },
                    { key: 'ancho', label: 'Ancho (m)', type: 'number', step: '0.01', width: '100px' },
                    { key: 'alto', label: 'Alto (m)', type: 'number', step: '0.01', width: '100px' },
                    { key: 'cantidad', label: 'Cantidad', type: 'number', width: '100px' },
                    { key: 'm3', label: 'm³', type: 'number', step: '0.01', disabled: true, width: '100px' }
                  ]
                )}
              </CardBody>
            </Card>

            <Row className="g-3">
              {/* Solo mostrar m3_gratis si especiales NO está seleccionado */}
              {!actaVisita.especiales && (
                <Col md={6}>
                  {(ocultarChecksM3 && !actaVisita.cancelado) ? (
                    <div className="alert alert-warning p-2">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      {motivoRestriccionM3 || 'Límite alcanzado para m³ gratis.'}
                      {(ultimaFechaOtorgado || proximoDesde) && (
                        <div className="mt-1 small">
                          {ultimaFechaOtorgado && (
                            <div>
                              <strong>Última fecha otorgado:</strong> {ultimaFechaOtorgado}
                            </div>
                          )}
                          {proximoDesde && (
                            <div>
                              <strong>Próximo desde:</strong> {proximoDesde}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      {permitir1m3 && (
                        <FormGroup check>
                          <Input
                            type="checkbox"
                            id="m3_gratis"
                            name="m3_gratis"
                            checked={actaVisita.m3_gratis}
                            onChange={handleActaChange}
                            disabled={isCasoFinalizado || actaVisita.cancelado}
                          />
                          <Label check for="m3_gratis">m³ Gratis</Label>
                        </FormGroup>
                      )}

                      {permitir2m3 && (
                        <FormGroup check className="mt-2">
                          <Input
                            type="checkbox"
                            id="dos_m3_gratis"
                            name="dos_m3_gratis"
                            checked={!!actaVisita.dos_m3_gratis}
                            onChange={handleActaChange}
                            disabled={isCasoFinalizado || actaVisita.cancelado}
                          />
                          <Label check for="dos_m3_gratis">2 m³ Gratis</Label>
                        </FormGroup>
                      )}
                      {!permitir1m3 && !permitir2m3 && (
                        <div className="alert alert-info p-2 mt-2">
                          <i className="fas fa-info-circle me-2"></i>
                          No hay m³ gratis disponibles por regla de 180 días.
                          {(ultimaFechaOtorgado || proximoDesde) && (
                            <div className="mt-1 small">
                              {ultimaFechaOtorgado && (
                                <div>
                                  <strong>Última fecha otorgado:</strong> {ultimaFechaOtorgado}
                                </div>
                              )}
                              {proximoDesde && (
                                <div>
                                  <strong>Próximo desde:</strong> {proximoDesde}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  {!actaVisita.cancelado && motivoRestriccionM3 && (
                    <small className="text-danger d-block mt-2">
                      <i className="fas fa-info-circle me-1"></i>
                      {motivoRestriccionM3}
                    </small>
                  )}
                </Col>
              )}
              {/* Si especiales está seleccionado, agregar columna vacía para mantener el layout */}
              {actaVisita.especiales && (
                <Col md={6}>
                  {/* Columna vacía para mantener el layout */}
                </Col>
              )}
              <Col md={6}>
                <FormGroup>
                  <Label>Valor m³ ($)</Label>
                  <Input
                    type="text"
                    name="valor_m3"
                    value={(() => {
                      const raw = actaVisita.valor_m3;
                      const num = parseFloat((raw ?? '').toString().replace(/,/g, '.'));
                      return isNaN(num)
                        ? '$0.00'
                        : `$${num.toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                    })()}
                    disabled={true}
                    placeholder="$0.00"
                    style={{ backgroundColor: '#f8f9fa', color: '#6c757d' }}
                  />
                  {tarifaActual && (
                    <div className="mt-2">
                      <small className="text-muted d-block">
                        Tarifa Normal: ${tarifaActual.valor_tarifa?.toLocaleString('es-CO')} | Especial: ${tarifaActual.tarifa_especial?.toLocaleString('es-CO')}
                      </small>
                      <small className="text-info d-block mt-1">
                        {(() => {
                          let recipientes = actaVisita.recipientes_detalle || [];
                          if (typeof recipientes === 'string') {
                            try { recipientes = JSON.parse(recipientes); } catch (e) { recipientes = []; }
                          }
                          if (!Array.isArray(recipientes)) { recipientes = []; }
                          const totalM3 = recipientes.reduce((total, recipiente) => total + (parseFloat(recipiente.m3) || 0), 0);
                          const descuentoM3 = !actaVisita.especiales
                            ? (actaVisita.dos_m3_gratis ? 2 : (actaVisita.m3_gratis ? 1 : 0))
                            : 0;
                          const m3Facturables = Math.max(0, totalM3 - descuentoM3);
                          const tarifaAplicarInline = (actaVisita.tarifa_lodo && tarifaActual.tarifa_lodo)
                             ? parseFloat(tarifaActual.tarifa_lodo)
                             : (actaVisita.especiales ? tarifaActual.tarifa_especial : tarifaActual.valor_tarifa);
                           const etiquetaDescuento = (!actaVisita.especiales && descuentoM3 > 0) ? `(${descuentoM3} m³ gratis aplicado)` : '';
                           const etiquetaTarifa = (actaVisita.tarifa_lodo && tarifaActual.tarifa_lodo) ? '(Lodo Acueducto)' : (actaVisita.especiales ? '(Especial)' : '(Normal)');
                           return `Cálculo: ${m3Facturables.toFixed(2)} m³ × $${tarifaAplicarInline?.toLocaleString('es-CO')} ${etiquetaTarifa} ${etiquetaDescuento}`;
                        })()}
                      </small>
                    </div>
                  )}
                </FormGroup>
              </Col>
            </Row>

            {/* Cancelado debajo de m³ gratis (Step 5 - Recipientes) */}
            <Row className="g-3 mt-2">
              <Col md={6}>
                <FormGroup check>
                  <Input
                    type="checkbox"
                    id="cancelado"
                    name="cancelado"
                    checked={actaVisita.cancelado}
                    onChange={handleActaChange}
                    disabled={isCasoFinalizado}
                  />
                  <Label check for="cancelado">Cancelado</Label>
                </FormGroup>
              </Col>
            </Row>


          </div>
        );

      case 6:
        return (
          <div>
            <div className="d-flex align-items-center mb-4">
              <div style={{
                backgroundColor: '#5b73e8',
                borderRadius: '8px',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '12px'
              }}>
                <i className="fas fa-comment-alt text-white"></i>
              </div>
              <h5 className="fw-bold mb-0" style={{ color: '#5b73e8' }}>Observaciones</h5>
            </div>

            <Row>
              <Col md={12}>
                <FormGroup>
                  <Label>{formatLabel('observaciones')}</Label>
                  <Input
                    type="textarea"
                    name="observaciones"
                    value={actaVisita.observaciones}
                    onChange={handleActaChange}
                    disabled={isCasoFinalizado}
                    rows={6}
                    placeholder="Escriba aquí las observaciones de la visita..."
                  />
                </FormGroup>
              </Col>
            </Row>
          </div>
        );

      case 7:
        return (
          <div>
            <div className="d-flex align-items-center mb-4">
              <div style={{
                backgroundColor: '#e83e8c',
                borderRadius: '8px',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '12px'
              }}>
                <i className="fas fa-signature text-white"></i>
              </div>
              <h5 className="fw-bold mb-0" style={{ color: '#e83e8c' }}>Firmas y Representantes</h5>
            </div>

            {/* Campo de Anotaciones */}
            <Row className="mb-4">
              <Col md={12}>
                <FormGroup>
                  <Label>{formatLabel('anotaciones')}</Label>
                  <Input
                    type="textarea"
                    name="anotaciones"
                    value={actaVisita.anotaciones}
                    onChange={handleActaChange}
                    disabled={isCasoFinalizado}
                    rows={4}
                    placeholder="Anotaciones del cliente..."
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row className="g-4">
              {/* Representante Usuario */}
              <Col lg={6}>
                <Card className="border-0 shadow-sm">
                  <CardBody>
                    <h6 className="fw-bold mb-3" style={{ color: '#e83e8c' }}>Representante Usuario</h6>
                    <FormGroup>
                      <Label>{formatLabel('representante_nombre')}</Label>
                      <Input
                        type="text"
                        name="representante_nombre"
                        value={actaVisita.sin_firma ? '' : actaVisita.representante_nombre}
                        onChange={handleActaChange}
                        disabled={isCasoFinalizado || actaVisita.sin_firma}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>{formatLabel('representante_cedula')}</Label>
                      <Input
                        type="text"
                        name="representante_cedula"
                        value={actaVisita.sin_firma ? '' : actaVisita.representante_cedula}
                        onChange={handleActaChange}
                        disabled={isCasoFinalizado || actaVisita.sin_firma}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>{formatLabel('representante_telefono_celular')}</Label>
                      <Input
                        type="tel"
                        name="representante_telefono_celular"
                        value={actaVisita.representante_telefono_celular}
                        onChange={handleActaChange}
                        disabled={isCasoFinalizado}
                      />
                    </FormGroup>

                    {/* Firma Representante */}
                    <FormGroup>
                      <Label className="fw-semibold">Firma del Representante</Label>
                      <div className="signature-container">
                        {/* Preview mejorado de firma */}
                        {(tempRepresentanteFirma || actaVisita.representante_firma || caso?.informe?.representante_firma) ? (
                          <div className="signature-preview-card border rounded-3 p-3 mb-3"
                            style={{
                              backgroundColor: '#f8f9fa',
                              border: '2px dashed #007bff !important',
                              transition: 'all 0.3s ease'
                            }}>
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <div className="d-flex align-items-center">
                                <i className="fas fa-check-circle text-primary me-2"></i>
                                <span className="fw-semibold text-primary">Firma Capturada</span>
                              </div>
                              {tempRepresentanteFirma && (
                                <span className="badge bg-warning text-dark">
                                  <i className="fas fa-clock me-1"></i>
                                  Sin guardar
                                </span>
                              )}
                            </div>
                            <div className="signature-image-container text-center p-2 bg-white rounded-2">
                              <img
                                src={
                                  tempRepresentanteFirma ?
                                    tempRepresentanteFirma :
                                    (actaVisita.representante_firma ?
                                      getBaseUrl() + actaVisita.representante_firma :
                                      getBaseUrl() + caso?.informe?.representante_firma)
                                }
                                alt="Firma del Representante"
                                className="signature-image"
                                style={{
                                  maxWidth: '250px',
                                  maxHeight: '100px',
                                  objectFit: 'contain',
                                  border: '1px solid #e9ecef',
                                  borderRadius: '4px'
                                }}
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="signature-placeholder border rounded-3 p-4 mb-3 text-center"
                            style={{
                              backgroundColor: '#f8f9fa',
                              border: '2px dashed #dee2e6 !important'
                            }}>
                            <i className="fas fa-signature text-muted mb-2" style={{ fontSize: '2rem' }}></i>
                            <p className="text-muted mb-0">No hay firma capturada</p>
                            <small className="text-muted">Haz clic en "Agregar Firma" para capturar la firma</small>
                          </div>
                        )}

                        {/* Botón para agregar/cambiar firma - solo en acta principal */}
                        {actaVisita.esPrincipal && (
                          <Button
                            color={(tempRepresentanteFirma || actaVisita.representante_firma || caso?.informe?.representante_firma) ? "outline-warning" : "primary"}
                            size="sm"
                            onClick={() => setShowRepresentanteSignature(true)}
                            disabled={isCasoFinalizado || actaVisita.sin_firma}
                            className="w-100"
                          >
                            <i className="fas fa-signature me-2"></i>
                            {(tempRepresentanteFirma || actaVisita.representante_firma || caso?.informe?.representante_firma) ? 'Cambiar Firma' : 'Agregar Firma'}
                          </Button>
                        )}
                      </div>
                    </FormGroup>
                  </CardBody>
                </Card>
              </Col>

              {/* Representante Ciudad Limpia */}
              <Col lg={6}>
                <Card className="border-0 shadow-sm">
                  <CardBody>
                    <h6 className="fw-bold mb-3" style={{ color: '#28a745' }}>Representante Ciudad Limpia</h6>
                    <FormGroup>
                      <Label>{formatLabel('ciudad_limpia_nombre')}</Label>
                      <Input
                        type="text"
                        name="ciudad_limpia_nombre"
                        value={actaVisita.ciudad_limpia_nombre}
                        onChange={handleActaChange}
                        disabled={isCasoFinalizado}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>{formatLabel('ciudad_limpia_codigo')}</Label>
                      <Input
                        type="text"
                        name="ciudad_limpia_codigo"
                        value={actaVisita.ciudad_limpia_codigo}
                        onChange={handleActaChange}
                        disabled={isCasoFinalizado}
                      />
                    </FormGroup>

                    {/* Firma Ciudad Limpia */}

                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="container-fluid">
      {caso && caso.fecha_vencimiento && caso.estado !== "FINALIZADO" && (() => {
        const d = new Date(caso.fecha_vencimiento).toISOString().slice(0, 10);
        const today = new Date().toISOString().slice(0, 10);
        let alertInfo = null;

        if (d < today) {
          alertInfo = { label: 'OT VENCIDA', color: 'danger', icon: 'fas fa-exclamation-triangle' };
        } else {
          const diffDays = Math.ceil((new Date(d) - new Date(today)) / (1000 * 60 * 60 * 24));
          if (diffDays >= 0 && diffDays <= 2) {
            alertInfo = { label: 'Próximo a Vencer', color: 'warning', icon: 'fas fa-clock' };
          }
        }

        if (alertInfo) {
          return (
            <div className={`alert alert-${alertInfo.color} d-flex align-items-center mb-3`} role="alert">
              <i className={`${alertInfo.icon} me-2 font-size-18`}></i>
              <div>
                <strong>{alertInfo.label}</strong> - La fecha de vencimiento es {caso.fecha_vencimiento}
              </div>
            </div>
          );
        }
        return null;
      })()}
      {/* Navegación de Actas */}
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Gestión de Actas de Visita</h5>
          <Button
            color="success"
            size="sm"
            onClick={agregarNuevaActa}
            disabled={isCasoFinalizado}
          >
            <i className="fas fa-plus me-1"></i>
            Agregar Acta
          </Button>
        </div>

        {/* Pestañas de Actas */}
        <div className="d-flex flex-wrap gap-2 mb-3">
          {actas.map((acta, index) => (
            <div key={acta.id} className="d-flex align-items-center">
              <Button
                color={acta.esPrincipal ?
                  (index === actaActualIndex ? 'warning' : 'outline-warning') :
                  (index === actaActualIndex ? 'primary' : 'outline-primary')
                }
                size="sm"
                onClick={() => cambiarActa(index)}
                className={`d-flex align-items-center ${acta.esPrincipal ? 'fw-bold border-2' : ''
                  }`}
                style={{
                  boxShadow: acta.esPrincipal ? '0 2px 8px rgba(255, 193, 7, 0.3)' : 'none'
                }}
              >
                {acta.esPrincipal ? (
                  <>
                    <i className="fas fa-crown me-1 text-warning" title="Acta Principal"></i>
                    <span className="text-uppercase">Principal</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-file-alt me-1" title="Acta Adicional"></i>
                    <span>Adicional {index}</span>
                  </>
                )}
              </Button>
              {!acta.esPrincipal && (
                <Button
                  color="danger"
                  size="sm"
                  outline
                  onClick={() => eliminarActa(index)}
                  className="ms-1"
                  disabled={isCasoFinalizado}
                  title="Eliminar acta"
                >
                  <i className="fas fa-times"></i>
                </Button>
              )}
            </div>
          ))}
        </div>

        {/* Información del Acta Actual */}
        <div className={`alert d-flex align-items-center justify-content-between ${actaVisita.esPrincipal ? 'alert-warning border-warning' : 'alert-info'
          }`}>
          <div className="d-flex align-items-center">
            {actaVisita.esPrincipal ? (
              <i className="fas fa-crown me-2 text-warning"></i>
            ) : (
              <i className="fas fa-file-alt me-2"></i>
            )}
            <span>
              <strong>Acta Actual:</strong> {actaVisita.titulo}
              {actaVisita.esPrincipal ? (
                <span className="badge bg-warning text-dark ms-2">
                  <i className="fas fa-star me-1"></i>
                  PRINCIPAL
                </span>
              ) : (
                <span className="badge bg-secondary ms-2">
                  <i className="fas fa-plus me-1"></i>
                  ADICIONAL
                </span>
              )}
            </span>
          </div>

          {/* Botón Ver PDF */}
          <Button
            onClick={openPdf}
            className="d-flex align-items-center"
            style={{
              fontSize: '0.8rem',
              padding: '0.4rem 0.8rem',
              borderRadius: '0.375rem',
              backgroundColor: '#dc2626',
              borderColor: '#dc2626',
              color: 'white',
              border: 'none',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#b91c1c';
              e.target.style.transform = 'translateY(-2px) scale(1.05)';
              e.target.style.boxShadow = '0 8px 20px rgba(220, 38, 38, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#dc2626';
              e.target.style.transform = 'translateY(0) scale(1)';
              e.target.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.3)';
            }}
            title="Ver preliminar del PDF"
          >
            <i className="fas fa-file-pdf me-2"
              style={{
                fontSize: '0.9rem',
                animation: 'pulse 2s infinite'
              }}></i>
            Ver PDF
          </Button>

          <style jsx>{`
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.7; }
            }
          `}</style>
        </div>
      </div>

      {/* Indicador de progreso */}
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span className="fw-bold">Progreso: {currentStep} de {totalSteps}</span>
          <span className="text-muted">{Math.round((currentStep / totalSteps) * 100)}%</span>
        </div>
        <div className="progress" style={{ height: '8px' }}>
          <div
            className="progress-bar bg-primary"
            style={{ width: `${(currentStep / totalSteps) * 100}%` }}
          ></div>
        </div>
      </div>

      {/* Navegación de pasos */}
      <div className="mb-4">
        <div className="d-flex flex-wrap gap-2">
          {stepTitles.map((title, index) => {
            const stepNumber = index + 1;
            const isActive = currentStep === stepNumber;
            const isCompleted = currentStep > stepNumber;
            const isBlocked = isStepBlocked(stepNumber);

            return (
              <Button
                key={stepNumber}
                size="sm"
                color={isBlocked ? 'danger' : isActive ? 'primary' : isCompleted ? 'success' : 'dark'}
                outline={!isActive && !isCompleted && !isBlocked}
                onClick={() => goToStep(stepNumber)}
                className="d-flex align-items-center"
                disabled={isBlocked}
                title={isBlocked ? `Paso bloqueado para tipo de caso: ${caso?.tipo_caso}` : ''}
              >
                <span className="me-1">{isBlocked ? <i className="fas fa-lock"></i> : stepNumber}</span>
                <span className="d-none d-md-inline">{title}</span>
              </Button>
            );
          })}
        </div>
      </div>

      {/* Contenido del paso actual */}
      <Card className="border-0 shadow-sm">
        <CardBody className="p-4">
          <Form>
            {renderStepContent()}
          </Form>
        </CardBody>
      </Card>

      {/* Botones de navegación */}
      <div className="d-flex justify-content-between mt-4">
        <div className="d-flex gap-2">
          <Button
            color="secondary"
            outline
            onClick={prevStep}
            disabled={currentStep === 1}
          >
            <i className="fas fa-chevron-left me-1"></i>
            Anterior
          </Button>

        </div>

        <div className="d-flex gap-2">
          {/* Botón específico para el acta actual */}
          {actaVisita.esPrincipal ? (
            // Botón para acta principal
            <Button
              color="warning"
              onClick={guardarActaPrincipal}
              className={actaPrincipalTieneCambios ? 'border-warning' : ''}
              style={{
                backgroundColor: actaPrincipalTieneCambios ? '#fff3cd' : '',
                borderColor: actaPrincipalTieneCambios ? '#ffc107' : ''
              }}
            >
              <i className="fas fa-crown me-1"></i>
              Guardar Acta Principal
              {actaPrincipalTieneCambios && <i className="fas fa-exclamation-circle ms-1 text-warning"></i>}
            </Button>
          ) : actaVisita.esNueva ? (
            // Botón para crear nueva acta adicional
            <Button
              color="success"
              onClick={guardarNuevaActaAdicional}
              disabled={actaPrincipalTieneCambios || !actaAdicionalEnProceso}
              className={actaAdicionalEnProceso ? 'border-info' : ''}
              style={{
                backgroundColor: actaAdicionalEnProceso ? '#d1ecf1' : '',
                borderColor: actaAdicionalEnProceso ? '#17a2b8' : ''
              }}
            >
              <i className="fas fa-save me-1"></i>
              Guardar Acta Adicional
              {actaAdicionalEnProceso && <i className="fas fa-exclamation-circle ms-1 text-warning"></i>}
            </Button>
          ) : (
            // Botón para actualizar acta adicional existente
            <Button
              color="info"
              onClick={actualizarActaAdicional}
            >
              <i className="fas fa-edit me-1"></i>
              Actualizar Acta Adicional
            </Button>
          )}

          {currentStep < totalSteps ? (
            <Button color="secondary" onClick={nextStep}>
              Siguiente
              <i className="fas fa-chevron-right ms-1"></i>
            </Button>
          ) : null}
        </div>
      </div>

      {/* Modales de firma */}
      {showRepresentanteSignature && (
        <SignatureModal
          isOpen={showRepresentanteSignature}
          onClose={() => setShowRepresentanteSignature(false)}
          onSave={handleRepresentanteSignature}
          title="Firma del Representante"
        />
      )}

      {showCiudadLimpiaSignature && (
        <SignatureModal
          isOpen={showCiudadLimpiaSignature}
          onClose={() => setShowCiudadLimpiaSignature(false)}
          onSave={handleCiudadLimpiaSignature}
          title="Firma Ciudad Limpia"
        />
      )}

      {/* Botón flotante para subir fotos del informe - Diseño Profesional */}
      <Button
        color="primary"
        className="rounded-circle shadow-lg floating-camera-btn"
        style={{
          position: 'fixed',
          bottom: '30px',
          right: '30px',
          width: '72px',
          height: '72px',
          zIndex: 1050,
          display: isCasoFinalizado ? 'none' : 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(145deg, #667eea 0%, #764ba2 100%)',
          border: '3px solid rgba(255, 255, 255, 0.2)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
          transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          cursor: 'pointer',
          overflow: 'hidden',
          animation: 'float 4s ease-in-out infinite'
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = 'translateY(-8px) scale(1.08)';
          e.target.style.boxShadow = '0 20px 50px rgba(102, 126, 234, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.4)';
          e.target.style.background = 'linear-gradient(145deg, #764ba2 0%, #667eea 100%)';
          e.target.style.animation = 'none';
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'translateY(0) scale(1)';
          e.target.style.boxShadow = '0 8px 32px rgba(102, 126, 234, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
          e.target.style.background = 'linear-gradient(145deg, #667eea 0%, #764ba2 100%)';
          e.target.style.animation = 'float 4s ease-in-out infinite';
        }}
        onClick={() => setShowUploadModal(true)}
        title="Subir fotos al informe"
        disabled={isCasoFinalizado}
      >
        <div style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <i className="fas fa-camera camera-icon" style={{
            fontSize: '1.8rem',
            color: 'white',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
            transition: 'all 0.3s ease'
          }}></i>
          {/* Efecto de brillo */}
          <div className="shine-effect" style={{
            position: 'absolute',
            top: '-50%',
            left: '-50%',
            width: '200%',
            height: '200%',
            background: 'linear-gradient(45deg, transparent, rgba(255,255,255,0.15), transparent)',
            animation: 'shine 4s infinite',
            pointerEvents: 'none'
          }}></div>
          {/* Partículas decorativas */}
          <div className="particle particle-1" style={{
            position: 'absolute',
            width: '4px',
            height: '4px',
            background: 'rgba(255,255,255,0.6)',
            borderRadius: '50%',
            top: '15%',
            right: '20%',
            animation: 'sparkle 2s infinite'
          }}></div>
          <div className="particle particle-2" style={{
            position: 'absolute',
            width: '3px',
            height: '3px',
            background: 'rgba(255,255,255,0.4)',
            borderRadius: '50%',
            bottom: '20%',
            left: '25%',
            animation: 'sparkle 2.5s infinite 0.5s'
          }}></div>
        </div>
      </Button>

      {/* Estilos CSS para animaciones profesionales */}
      <style jsx>{`
        @keyframes shine {
          0% {
            transform: translateX(-100%) translateY(-100%) rotate(45deg);
          }
          50% {
            transform: translateX(100%) translateY(100%) rotate(45deg);
          }
          100% {
            transform: translateX(-100%) translateY(-100%) rotate(45deg);
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-8px);
          }
        }
        
        @keyframes sparkle {
          0%, 100% {
            opacity: 0;
            transform: scale(0);
          }
          50% {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        /* Efecto de respiración sutil */
        @keyframes breathe {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.02);
          }
        }
        
        /* Animación adicional para el ícono */
        .floating-camera-btn:hover .camera-icon {
          animation: pulse-icon 0.6s ease-in-out;
        }
        
        @keyframes pulse-icon {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
        }
        
        /* Efecto de ondas al hacer hover */
        .floating-camera-btn::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.1);
          transform: translate(-50%, -50%);
          transition: width 0.6s, height 0.6s;
        }
        
        .floating-camera-btn:hover::before {
          width: 120%;
          height: 120%;
        }
      `}</style>

      {/* Modal de subida de fotos */}
      <Modal isOpen={showUploadModal} toggle={() => setShowUploadModal(false)} size="lg">
        <ModalHeader toggle={() => setShowUploadModal(false)}>
          Subir Fotos al Informe
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <div className="d-flex align-items-center">
              <i className="fas fa-info-circle text-primary me-2"></i>
              <small className="text-muted">
                Arrastra y suelta imágenes aquí o haz clic para seleccionar archivos. También puedes usar la cámara.
              </small>
            </div>
          </div>

          {/* Imágenes ya cargadas en el informe */}
          <div className="mb-4">
            <h6 className="fw-bold d-flex align-items-center mb-2">
              <i className="fas fa-images text-success me-2"></i>
              Imágenes cargadas en este informe
              {existingImages && existingImages.length > 0 ? (
                <span className="badge bg-success ms-2">{existingImages.length}</span>
              ) : null}
            </h6>
            {isLoadingExistingImages ? (
              <div className="text-muted"><small>Cargando imágenes...</small></div>
            ) : existingImages && existingImages.length > 0 ? (
              <>
                {isAdministrador && (
                  <div className="mb-2 d-flex justify-content-end">
                    <Button
                      color="danger"
                      outline
                      size="sm"
                      onClick={() => deleteImages(selectedImageIds)}
                      disabled={selectedImageIds.length === 0 || isDeletingImages}
                      title={selectedImageIds.length === 0 ? 'Seleccione imágenes para eliminar' : 'Eliminar imágenes seleccionadas'}
                    >
                      <i className="fas fa-trash-alt me-2"></i>
                      Eliminar seleccionadas {selectedImageIds.length > 0 && `(${selectedImageIds.length})`}
                    </Button>
                  </div>
                )}
                <Row className="g-3">
                  {existingImages.map((img, idx) => {
                    const url = resolveImageUrl(img.image || img.image_url || img);
                    const imgId = img.id || `temp-${idx}`;
                    const isSelected = selectedImageIds.includes(imgId);
                    const canDelete = img.id; // Solo se puede eliminar si tiene ID real
                    return (
                      <Col md={3} sm={4} xs={6} key={`existing-${imgId}`}>
                        <div
                          className="border rounded-3 p-2 position-relative"
                          style={{ backgroundColor: '#f8f9fa', boxShadow: isSelected ? '0 0 0 2px #dc3545' : undefined }}
                        >
                          {isAdministrador && (
                            <Input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelectImage(imgId)}
                              className="position-absolute"
                              style={{ top: '8px', left: '8px', zIndex: 10 }}
                              title={canDelete ? (isSelected ? 'Quitar de selección' : 'Seleccionar para eliminar') : 'Esta imagen no se puede eliminar'}
                              disabled={!canDelete}
                            />
                          )}
                          <img
                            src={url}
                            alt={`Imagen ${imgId || idx + 1}`}
                            style={{ width: '100%', height: '140px', objectFit: 'cover', borderRadius: '4px' }}
                            onError={() => console.error('Error cargando imagen existente:', url)}
                          />
                          <Button
                            color="primary"
                            size="sm"
                            outline
                            className="position-absolute"
                            style={{ top: '8px', right: isAdministrador ? '36px' : '8px', padding: '0 6px' }}
                            onClick={() => window.open(url, '_blank')}
                            title="Abrir en nueva pestaña"
                          >
                            <i className="fas fa-external-link-alt"></i>
                          </Button>
                          {isAdministrador && canDelete && (
                            <Button
                              color="danger"
                              size="sm"
                              outline
                              className="position-absolute"
                              style={{ top: '8px', right: '8px', padding: '0 6px', zIndex: 10 }}
                              onClick={() => deleteSingleImage(img.id)}
                              title="Eliminar imagen"
                              disabled={isDeletingImages}
                            >
                              <i className="fas fa-times"></i>
                            </Button>
                          )}
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </>
            ) : (
              <div className="text-muted"><small>No hay imágenes cargadas aún para este informe.</small></div>
            )}
          </div>

          {/* Área de arrastrar y soltar */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            className="border rounded-3 p-4 text-center mb-3"
            style={{
              border: `2px dashed ${isDragOver ? '#0d6efd' : '#ced4da'}`,
              backgroundColor: isDragOver ? '#e9f5ff' : '#f8f9fa',
              transition: 'all 0.2s ease-in-out',
              cursor: 'pointer'
            }}
          >
            <i className="fas fa-cloud-upload-alt text-primary mb-2" style={{ fontSize: '2rem' }}></i>
            <p className="mb-1">Arrastra y suelta imágenes aquí</p>
            <small className="text-muted">JPG, PNG o HEIC. Máximo 10 archivos por carga.</small>
          </div>

          {/* Acciones de selección */}
          <div className="d-flex gap-2 mb-4">
            <Button color="secondary" outline onClick={() => fileInputRef.current && fileInputRef.current.click()}>
              <i className="fas fa-folder-open me-2"></i>
              Seleccionar desde archivos
            </Button>
            <Button color="success" outline onClick={handleCameraClick}>
              <i className="fas fa-camera me-2"></i>
              Usar cámara
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept="image/*"
              multiple
              onChange={handleFileSelect}
            />
          </div>

          {/* Vista previa de archivos seleccionados */}
          {selectedFiles.length > 0 ? (
            <div className="mb-3">
              <h6 className="fw-bold">Imágenes seleccionadas ({selectedFiles.length})</h6>
              <Row className="g-3 mt-1">
                {selectedFiles.map((fileObj, index) => (
                  <Col md={3} sm={4} xs={6} key={`preview-${index}`}>
                    <div className="border rounded-3 p-2 position-relative" style={{ backgroundColor: '#f8f9fa' }}>
                      <img
                        src={fileObj.preview}
                        alt={fileObj.name}
                        style={{ width: '100%', height: '140px', objectFit: 'cover', borderRadius: '4px' }}
                      />
                      <Button
                        color="danger"
                        size="sm"
                        outline
                        className="position-absolute"
                        style={{ top: '8px', right: '8px', padding: '0 6px' }}
                        onClick={() => removeSelectedFile(index)}
                        title="Eliminar"
                      >
                        <i className="fas fa-times"></i>
                      </Button>
                      <div className="mt-2">
                        <small className="text-truncate d-block" title={fileObj.name}>{fileObj.name}</small>
                        <small className="text-muted">{(fileObj.size / (1024 * 1024)).toFixed(2)} MB</small>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          ) : (
            <div className="text-center text-muted">
              <small>No hay imágenes seleccionadas.</small>
            </div>
          )}

          {/* Barra de progreso */}
          {isUploading && (
            <div className="mt-3">
              <div className="d-flex justify-content-between mb-1">
                <small className="text-muted">Subiendo imágenes...</small>
                <small className="text-muted">{uploadProgress}%</small>
              </div>
              <div className="progress" style={{ height: '8px' }}>
                <div
                  className="progress-bar bg-success"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setShowUploadModal(false)} disabled={isUploading}>
            Cancelar
          </Button>
          <Button
            color="primary"
            onClick={handleImageUpload}
            disabled={selectedFiles.length === 0 || isUploading}
          >
            <i className="fas fa-upload me-2"></i>
            Subir imágenes
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal de cámara */}
      <Modal isOpen={showCameraModal} toggle={closeCameraModal} size="lg" centered>
        <ModalHeader toggle={closeCameraModal}>
          <i className="fas fa-camera me-2"></i>
          Tomar Foto
        </ModalHeader>
        <ModalBody className="text-center p-0">
          <div style={{ position: 'relative', backgroundColor: '#000' }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                height: '400px',
                objectFit: 'cover'
              }}
            />
            <canvas
              ref={canvasRef}
              style={{ display: 'none' }}
            />

            {/* Botón de captura superpuesto */}
            <div
              style={{
                position: 'absolute',
                bottom: '20px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 10
              }}
            >
              <Button
                color="light"
                size="lg"
                onClick={takePhoto}
                style={{
                  borderRadius: '50%',
                  width: '70px',
                  height: '70px',
                  border: '4px solid white',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.3)'
                }}
              >
                <i className="fas fa-camera" style={{ fontSize: '1.5rem' }}></i>
              </Button>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeCameraModal}>
            <i className="fas fa-times me-2"></i>
            Cancelar
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal de Homologación de Cuenta Enel */}
      <Modal isOpen={showHomologarModal} toggle={() => setShowHomologarModal(false)} centered>
        <ModalHeader toggle={() => setShowHomologarModal(false)} className="bg-light">
          <i className="mdi mdi-swap-horizontal me-2 text-warning"></i> Homologar Cuenta ENEL
        </ModalHeader>
        <ModalBody className="p-4">
          <Form>
            <FormGroup>
              <Label for="nuevaCuentaEnelActa" className="fw-bold text-muted text-uppercase small">Nueva Cuenta ENEL</Label>
              <Input
                type="text"
                id="nuevaCuentaEnelActa"
                value={nuevaCuentaEnel}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '');
                  setNuevaCuentaEnel(val);
                }}
                placeholder="Ingrese el nuevo número de cuenta"
                inputMode="numeric"
                className="form-control-lg border-warning"
              />
              <small className="text-muted mt-2 d-block">
                <i className="mdi mdi-information me-1"></i> El número de cuenta debe contener solo dígitos.
              </small>
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter className="bg-light border-0">
          <Button color="link" className="text-muted text-decoration-none" onClick={() => setShowHomologarModal(false)}>
            Cancelar
          </Button>
          <Button color="warning" className="px-4" onClick={handleSubmitHomologacion}>
            <i className="mdi mdi-check me-1"></i> Confirmar
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ActaVisita;
